"use client"
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog"
import { Button } from "../../Components/UI/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../Components/UI/tooltip"
import { Card, CardContent } from "../../Components/UI/card"
import { Badge } from "../../Components/UI/badge"
import { X, Plus, User, Star, Check, Pencil, ArrowLeft, ListTodo, History, ClipboardList, Paperclip, MessageSquare, MapPin, MessageSquareText } from "lucide-react"
import { FaSackDollar } from "react-icons/fa6"
import { BsBellFill, BsWhatsapp } from "react-icons/bs";
import { useCurrency } from "../../Hooks/useCurrency";
import ThreadSahiIcon from '../../Components/Common/ThreadSahiIcon';
import Upcoming from "../../Components/Common/Upcoming";
import ThreadDropdown from "../Threads/AddThreadDropdown"
import TaskDetailDialog from "../Tasks/TaskDetails"
import ThreadEntry from "../Threads/ThreadEntry"
import TransactionEntry from "./Tabs/TransactionEntry"
import ContractEntry from "../Contracts/ContractEntry"
import ConnectionEntryDialog from "./ConnectionEntry"
import TaskEntry from "../Tasks/TaskEntry"
import RecordEntry from "../Records/RecordsEntry"
import { TaskCompleteDialog } from "../../Components/Common/TaskCompleteDialog"
import ReassignDialog from "../Threads/ReassignDialog"
import { useDispatch, useSelector } from "react-redux"
import { getUsersArr, getThreadTypesArr, getAgencyDetails, getContractsArr, getActiveThreadTypesArr, checkIfInqOnlyMode } from "../../Store/Reducers"
import { useDesktop } from "../../Components/Common/IsDesktop"
import { AlertBox } from "../../Components/Common/AlertBox";
import {
  getRelativeDateText, getTaskColor, getColorClass, getPriorityInfo, getDateWithoutTime, getThreadTypeMeta, getCurrencySymbol, FormatAmount, getTransactionIconColorClass,
  getTransactionIcon, currentUserHasPermission, getThreadNameFromTypeID, sentimentEmoji, getCurrentUser, getFdbkScoreText, isPendingTasks,
  formatRecordText,
  canEditTask,
  userHasPermissionForAgency,
  getMyAgenciesMappedIDs,
  getUploadUrl,
  playTaskSuccessSound
} from "../../Utils/Utilities"
import { getAuthData } from "../../Utils/AuthUtils";
import { getStorageData } from "../../Utils/Storage";
import apiService from "../../Services/APIService";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../Components/UI/table"
import { getConnectionDetailAPI, manageFdbkRequestAPI, manageConnectionAccessAPI } from "./ConnectionAPI"
import RecordHistoryDialog from "./Tabs/RecordHistory"
import { format, isSameDay } from 'date-fns';
import ContactActions from "../../Components/Common/ContactActions"
import { completeTaskAPI, cancelTaskAPI, rejectTaskAPI, remindTaskAPI } from '../Tasks/TasksAPI';
import { toast } from "sonner";
import TaskHistoryDialog from "./Tabs/TaskHistory";
import { getPriorityDetails } from '../../Utils/Constants';
import { useTax } from "../../Hooks/useTax"
import { useNavigate } from "react-router-dom"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@radix-ui/react-hover-card"
import OtherContactsEntry from "./OtherContactsEntry";
import { useConnectionByIDAnyAgency, useThreadsByConnID, useActiveThreadsByConnID, useTasksByConnID, useContactsByConnID } from "../../Hooks/useConnectionData"
import { getFromIndexedDB } from "../../Utils/IndexedDBUtils";
import TransactionDropdown from './TransactionDropdown';
import ContractDropdown from './ContractDropdown';
import { getTransactionTypeById } from '../../Utils/Utilities';
import { FileText } from 'lucide-react';
import CompleteThreadDialog from "../../Components/Common/CompleteThreadDialog"
import AttachmentPreviewModal from "../../Components/Common/AttachmentPreviewModal";
import CustomSearchableDropdown from "../../Components/Common/CustomSearchableDD"
import CancelThreadDialog from "../../Components/Common/CancelThreadDialog"
import ContractDetailDialog from "./Tabs/ContractDetails"
import FeedbackSection from "../Threads/ThreadDetails/FeedbackSection";
import Policies from "../Settings/Policies/Policies"
import { setGlobalLoading } from "../../Store/Actions"
import { getThreadModeTitle } from "../../Utils/Utilities"
import NoDataCard from "../../Components/Common/NoDataCard"
import ThrDetails from "../Threads/ThrDetails"
import ThrDetailsInfo from "../Threads/ThrDetails/ThrDetailsInfo"
import ThrDetailsStatus from "../Threads/ThrDetails/ThrDetailsStatus"
import ThrDetailsActions from "../Threads/ThrDetails/ThrDetailsActions"
import { BsEnvelopeFill } from "react-icons/bs"
import { MdPhone } from "react-icons/md"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "../../Components/UI/alert-dialog"
import { performThreadActionAPI, reassignThreadAPI, updateThreadStageAPI, manageThreadAPI } from "../Threads/ThreadsAPI"

// Common Tailwind classes to prevent repetition
const connDetailsClasses = {
  button: "h-7 w-7 p-0 shrink-0",
  buttonGhost: "variant-ghost size-sm",
  tableCell: "px-3 py-2 text-sm",
  textColor: "text-color",
  dialogHeader: "table-header-text whitespace-nowrap px-3 py-2 text-color",
  textSubColor: "text-sub-text-color",
  badgeBase: "sahi-badge transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-card hover:bg-card/80",
  cardBase: "bg-white sahi-radius border border-sahi-border-color !max-h-[150px]",
  cardInteractionBase: "bg-white sahi-radius border border-sahi-border-color",
  flexCenter: "flex items-center justify-center",
  flexBetween: "flex items-center justify-between",
  loadingSpinner: "animate-spin sahi-radius border-2 border-t-transparent",
  hoverBg: "hover:bg-gray-50 cursor-pointer",
  truncate: "truncate",
  emptyStateContainer: "py-6 text-sub-text-color",
  actionButton: "text-sahi-green hover:bg-sahi-green-light",
  cancelButton: "text-sahi-red hover:bg-sahi-red-light",
  editButton: "text-sahi-blue hover:bg-sahi-blue-light",
  trueActionButton: " h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-green-light",
  falseActionButton: "h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-red-light",
  editActionButton: "h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-blue-light",
};


// Main component that displays connection details in a dialog with tabs for threads, tasks, and interactions
const ConnectionDetails = ({ isOpen, onClose, connID, activeTab = "Overview", expandThreadID }) => {
  const dispatch = useDispatch();
  const threadStages = useSelector((state) => state.masterData.threadStages);
  const currencySymbol = useCurrency();
  const taxIDLabel = useTax();
  const [editingThreadID, setEditingThreadID] = useState(null);
  const [attachDocDialog, setAttachDocDialog] = useState(false);
  const [completedThreadForAttachment, setCompletedThreadForAttachment] = useState(null);
  const attachFileInputRef = useRef(null);
  const [previewFilePath, setPreviewFilePath] = useState(null);
  const [isFullPreviewOpen, setIsFullPreviewOpen] = useState(false);
  const [isFullPreviewLoading, setIsFullPreviewLoading] = useState(true);
  const [fullPreviewUrl, setFullPreviewUrl] = useState("");
  const [businessCardRatio, setBusinessCardRatio] = useState(null);
  const hasExpandedRef = useRef(false);
  // TasksTable (and the other section components below) are defined inline and rendered via
  // <Component key={key} /> in renderTablesInOrder - since that function reference is recreated
  // on every ConnectionDetails render, React treats it as a brand-new component type and fully
  // remounts it whenever this component re-renders (e.g. opening the task detail dialog), which
  // resets the tasks list's own scroll position. This ref lives on the stable parent instead, so
  // it survives that remount and can be used to restore the scroll position right after.
  const tasksScrollTopRef = useRef(0);

  useEffect(() => {
    if (!isOpen) hasExpandedRef.current = false;
  }, [isOpen]);
  const [editingThreadTypeID, setEditingThreadTypeID] = useState(null);
  const users = useSelector(getUsersArr)
  const threadTypesData = useSelector(getThreadTypesArr)
  const navigate = useNavigate();
  // Use custom hooks for reactive data
  // isOwnAgencyConnection is false when this connection belongs to another of the user's agencies
  // (e.g. opened from a cross-agency task) - view-only in that case, all mutation actions below are disabled.
  const { connection, isOwnAgencyConnection } = useConnectionByIDAnyAgency(connID);
  const businessCardPath = connection?.ContactCard || connection?.Attachment || connection?.attachment;
  const connTypeStr = (connection?.ConnType || "").toString().toUpperCase();
  const isSupplierOrOther = connTypeStr.includes("S") || connTypeStr.includes("O") || connTypeStr.includes("SUPPLIER") || connTypeStr.includes("OTHER");

  useEffect(() => {
    setBusinessCardRatio(null);
  }, [businessCardPath]);
  const resolvedUsers = useMemo(() => {
    if (!connection?.AgencyID) return users;
    try {
      const allUsers = getStorageData('U') || [];
      const agencyUsers = allUsers.filter(u => String(u.AgencyID) === String(connection.AgencyID));
      if (agencyUsers.length > 0) {
        return agencyUsers.map(u => ({
          userID: u.UserID,
          name: u.Name,
          isAdmin: u.IsAdmin,
          fullAccess: u.FullAccess,
          connViewTypeID: u.ConnViewTypeID
        }));
      }
    } catch (e) {
      console.error('Error resolving agency users:', e);
    }
    return users;
  }, [users, connection?.AgencyID]);
  const contractsFromRedux = useSelector(getContractsArr);
  const canEditConnections = userHasPermissionForAgency('ConnEdit', connection?.AgencyID);
  const canDeleteConnections = userHasPermissionForAgency('ConnDel', connection?.AgencyID);
  const canCreateConnections = userHasPermissionForAgency('ConnEntry', connection?.AgencyID);
  const activeThreadTypesArr = useSelector(getActiveThreadTypesArr);
  const currActiveThreadType = getThreadModeTitle(activeThreadTypesArr);

  const { data: activeData } = useSelector((state) => state.activeData) || {};

  // Check if user can edit threads (ThrActions 1 or 2)
  const canEditThread = userHasPermissionForAgency('ThrActions', connection?.AgencyID);
  const canShowEditButton = isOwnAgencyConnection && canEditThread <= 2 && canEditThread > 0;
  // Check if user can create threads (ThrActions 1 or 3)
  const xnActions = userHasPermissionForAgency('XnActions', connection?.AgencyID);
  const perm = userHasPermissionForAgency('CtrcActions', connection?.AgencyID);
  const canEditContracts = (perm === 1 || perm === 2);
  const canEditXn = (xnActions === 1 || xnActions === 2);
  const canAddCtrc = (perm != 0 || perm <= 3);

  const thrActions = userHasPermissionForAgency('ThrActions', connection?.AgencyID);
  const canAddThr = isOwnAgencyConnection && thrActions >= 1 && thrActions <= 3;

  const hasAgencyAccess = useMemo(() => {
    if (!connection?.AgencyID) return false;
    const myAgenciesData = getStorageData('MyAgencies') || [];
    return myAgenciesData.some(agn => String(agn.AgencyID) === String(connection.AgencyID));
  }, [connection?.AgencyID]);

  const [threadsFromDB, setThreadsFromDB] = useState([]);

  useEffect(() => {
    if (!connID || isOwnAgencyConnection) {
      setThreadsFromDB([]);
      return;
    }

    const loadThreads = async () => {
      try {
        const dbThreads = await getFromIndexedDB('Threads') || [];
        const openIDs = connection?.OpenThreadIDs ? connection.OpenThreadIDs.split(',').map(id => id.trim()) : [];
        const related = dbThreads.filter(t =>
          String(t.ConnID) === String(connID) ||
          String(t.connID) === String(connID) ||
          openIDs.includes(String(t.ThreadID)) ||
          openIDs.includes(String(t.threadID))
        );
        setThreadsFromDB(related);
      } catch (e) {
        console.error('Error loading threads from IndexedDB fallback:', e);
      }
    };

    loadThreads();
  }, [connID, connection?.OpenThreadIDs, isOwnAgencyConnection]);

  const rawAllThreads = useThreadsByConnID(connID);
  const allThreads = useMemo(() => {
    return isOwnAgencyConnection ? rawAllThreads : threadsFromDB;
  }, [rawAllThreads, threadsFromDB, isOwnAgencyConnection]);

  const rawActiveThreads = useActiveThreadsByConnID(connID);
  const activeThreads = useMemo(() => {
    if (isOwnAgencyConnection) return rawActiveThreads;
    return threadsFromDB.filter(t => Number(t.StatusID || t.statusID || t.Sts || t.sts || 0) !== 2 && Number(t.StatusID || t.statusID || t.Sts || t.sts || 0) !== 3);
  }, [rawActiveThreads, threadsFromDB, isOwnAgencyConnection]);
  const rawTasks = useTasksByConnID(connID);
  const tasks = useMemo(() => {
    let list = rawTasks;
    if ((!list || list.length === 0) && connection?.Tasks) {
      try {
        const parsed = typeof connection.Tasks === 'string' ? JSON.parse(connection.Tasks) : connection.Tasks;
        if (Array.isArray(parsed)) {
          list = parsed;
        }
      } catch (e) {
        console.error('Error parsing tasks from connection:', e);
      }
    }
    // Filter active tasks only (Sts === 1)
    return list.filter(task => Number(task.Sts || task.status || 0) === 1);
  }, [rawTasks, connection?.Tasks]);
  const contacts = useContactsByConnID(connID);
  const [taskActionLoading, setTaskActionLoading] = useState({});
  const [selectedTaskForRecord, setSelectedTaskForRecord] = useState(null);
  const [editingTaskID, setEditingTaskID] = useState(null);
  const [taskEntryMode, setTaskEntryMode] = useState('normal'); // 'normal' | 'repeat'
  const [repeatPrefillData, setRepeatPrefillData] = useState(null);
  const pendingRepeatAfterRecordRef = useRef(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedContractForDetail, setSelectedContractForDetail] = useState(null);
  const [threadDetailDialog, setThreadDetailDialog] = useState(false);
  const [taskDetailDialog, setTaskDetailDialog] = useState(false);
  const [contractDetailDialog, setContractDetailDialog] = useState(false);
  const [threadActionLoading, setThreadActionLoading] = useState({});
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionPrefilledData, setTransactionPrefilledData] = useState({});
  const [selectedTransactionType, setSelectedTransactionType] = useState(1);

  const [selectedContract, setSelectedContract] = useState(null);
  const [contractPrefilledData, setContractPrefilledData] = useState({});
  const [selectedContractType, setSelectedContractType] = useState('sold');

  const threadTableScrollPositionRef = useRef(0);
  const threadScrollableRef = useRef(null);

  const [transactionDropdownOpen, setTransactionDropdownOpen] = useState(false);
  const [contractDropdownOpen, setContractDropdownOpen] = useState(false);
  const [selectedThreadForAction, setSelectedThreadForAction] = useState(null);

  const [thrEntryMode, setThrEntryMode] = useState(null);
  const [removeAccessState, setRemoveAccessState] = useState({ open: false, userID: null });
  var agencyData = useSelector(getAgencyDetails)
  const masterData = useSelector((state) => state.masterData)
  const regions = masterData?.regions;
  const isDesktop = useDesktop();
  const currentUser = getCurrentUser();
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  const showModule = { transactions: agencyData?.EnblXns, contracts: agencyData?.EnblContracts };

  const authData = getAuthData();
  const currentUserID = authData?.UserID;

  const isReminderSentToday = (lastRemitterDate) => {
    if (!lastRemitterDate) return false;
    const lastReminderDate = new Date(lastRemitterDate);
    const today = new Date();
    return (
      lastReminderDate.getFullYear() === today.getFullYear() &&
      lastReminderDate.getMonth() === today.getMonth() &&
      lastReminderDate.getDate() === today.getDate()
    );
  };

  const stageMap = useMemo(() => {
    if (!threadStages?.[0]?.Stages) {
      return {
        1: { name: "Received", bg: "bg-sahi-badge-bg-color", text: connDetailsClasses.textColor },
        2: { name: "Qualified", bg: "bg-sahi-badge-bg-color", text: connDetailsClasses.textColor },
        3: { name: "Discussion", bg: "bg-sahi-badge-bg-color", text: connDetailsClasses.textColor },
        4: { name: "Demo", bg: "bg-sahi-badge-bg-color", text: connDetailsClasses.textColor },
        5: { name: "Proposal", bg: "bg-sahi-badge-bg-color", text: connDetailsClasses.textColor },
        6: { name: "Decided", bg: "bg-sahi-badge-bg-color", text: connDetailsClasses.textColor },
        7: { name: "Done", bg: "bg-sahi-badge-bg-color", text: connDetailsClasses.textColor },
      };
    }

    const dynamicMap = {};
    threadStages[0].Stages.forEach(stage => {
      dynamicMap[stage.SID] = {
        name: stage.S,
        bg: "bg-sahi-badge-bg-color",
        text: connDetailsClasses.textColor
      };
    });

    return dynamicMap;
  }, [threadStages, connDetailsClasses.textColor]);

  const getStageInfo = useCallback((currStageID) => {
    return stageMap[currStageID] || {
      name: "Active",
      bg: "bg-sahi-badge-bg-color",
      text: connDetailsClasses.textColor,
    };
  }, [stageMap, connDetailsClasses.textColor]);

  const handleRemindTask = async (task) => {
    if (isReminderSentToday(task.LastRmdrDT)) {
      toast.error("You can only send one reminder for this task per day");
      return;
    }

    try {
      setTaskActionLoading(prev => ({ ...prev, [task.TskID]: 'reminding' }));

      const taskData = {
        taskID: task.TskID,
        connID: task.ConnID || 0,
        pocUserID: task.UID,
        taskName: task.Ttl,
        description: task.Txt,
        priority: task.Pri,
        status: task.Sts,
        taskType: task.Typ,
        threads: task.TIDs
      };

      await remindTaskAPI(taskData);
      toast.success(`Reminder sent to ${getExecutiveName(task.UID)}.`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(
        error.message || 'Failed to send reminder. Please try again.'
      );
    } finally {
      setTaskActionLoading(prev => ({ ...prev, [task.TskID]: null }));
    }
  };

  // Get active threads data (excluding Sales Orders: TypeID 2)
  const getThreadsData = () => activeThreads.filter(t => Number(t.TypeID || t.typeID || 0) !== 2);

  // Get active tasks data
  // Get active tasks data with role-based filtering
  const getTasksData = () => {
    if (currentUser?.IsAdmin) return tasks;

    // Filter tasks: User must be either the Creator or the assigned Executive (POC)
    return tasks.filter(task =>
      Number(task.UID) === Number(currentUserID) ||
      Number(task.CreatedUID) === Number(currentUserID)
    );
  };

  const [dialogStates, setDialogStates] = useState({
    threadEntry: false,
    taskEntry: false,
    transactionEntry: false,
    contractEntry: false,
    contractRenewal: false,
    contractCancellation: false,
    recordEntry: false,
    editDialog: false,
    editContactsDialog: false,
    otherContacts: false,
    recordHistory: false,
    recordTypeFilter: null,
    taskHistory: false,
    upcoming: false,
    confirmCompleteAlert: false,
    confirmCancelAlert: false,
    confirmThreadCompleteAlert: false,
    confirmThreadCancelAlert: false,
    fdbkRequest: false,
    isInquiryOnlyMode: isInqOnlyMode,
    prefilledData: null,
    threadDetails: false,
    ordHistoryEdit: false,
    thrDetails: false,
    reassignDialog: false,
    convertToSales: false,
    accessDialog: false,
  })

  const [accessUserIDs, setAccessUserIDs] = useState([]);
  const [localStates, setLocalStates] = useState({
    copiedText: "",
    selectedThreadType: activeThreadTypesArr.length > 1 ? 1 : activeThreadTypesArr[0]?.TID || 1,
    loadingRecords: false,
  })

  const [records, setRecords] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [feedbackThreadDetail, setFeedbackThreadDetail] = useState({});
  const [contractsFromAPI, setContractsFromAPI] = useState([]);
  const [ordersHistory, setOrdersHistory] = useState([]);
  const [userPendingRIDs, setUserPendingRIDs] = useState([]); // Store user's assigned pending RIDs

  const openOrders = useMemo(() => {
    return activeThreads.filter(t => Number(t.TypeID || t.typeID || 0) === 2);
  }, [activeThreads]);

  const allOrders = useMemo(() => {
    const historyIDs = new Set(ordersHistory.map(o => String(o.ThreadID || o.threadID)));
    const uniqueOpen = openOrders.filter(o => !historyIDs.has(String(o.ThreadID || o.threadID)));
    return [...uniqueOpen, ...ordersHistory];
  }, [openOrders, ordersHistory]);

  const { ownEmail } = useSelector(state => state.masterData);
  const hasOwnEmail = ownEmail && ownEmail.length > 0 && ownEmail[0]?.length > 0;

  // Get record types for interaction filtering
  const recordTypes = useSelector((state) => state.masterData?.recordTypes || []);
  const interactionRecordTypeNames = useMemo(() => {
    return new Set(
      recordTypes
        .filter(rt => rt.IsInxn === true || rt.IsInxn === 1)
        .map(rt => rt.Typ)
    );
  }, [recordTypes]);

  // Parse rejection reasons for the current thread type
  const rejectionReasonOptions = useMemo(() => {
    if (!selectedThreadForAction || !masterData?.rejectionReasons) return []

    const currentThreadType = selectedThreadForAction.TypeID
    const threadReasons = masterData.rejectionReasons.find(item => item.TID === currentThreadType)

    if (!threadReasons) return []

    try {
      const reasons = JSON.parse(threadReasons.Rsns)
      return reasons.map(reason => ({
        value: reason.RID,
        label: reason.R,
      }))
    } catch (error) {
      console.error("Error parsing failure reasons:", error)
      return []
    }
  }, [selectedThreadForAction, masterData?.rejectionReasons])

  const lastFeedbackDT = connection?.LastFdbkReqDT ? new Date(connection.LastFdbkReqDT) : null;
  const isFeedbackSentToday = lastFeedbackDT && isSameDay(lastFeedbackDT, new Date());


  // Helper function to get stage info for display
  const getStageInfoForDisplay = (typeID, stageID) => {
    const thread = threadStages?.find(thread => thread.TID === typeID);
    if (!thread) return null;
    const stage = thread.Stages?.find(stage => stage.SID === stageID);
    if (!stage) return null;
    return { SID: stage.SID, name: stage.S };
  };

  // Fetch pending records from activeData and extract RIDs when component loads
  useEffect(() => {
    if (!isOpen || !activeData) {
      setUserPendingRIDs([]);
      return;
    }

    try {
      const pendingData = JSON.parse(activeData?.PendingRecords || '[]');

      const userPendingData = Array.isArray(pendingData)
        ? pendingData.find(item => item.UID === Number(currentUserID))
        : null;

      if (userPendingData && Array.isArray(userPendingData.PndgRecs)) {
        const allRIDs = [];

        userPendingData.PndgRecs.forEach(record => {
          if (record.RIDs) {
            const rids = record.RIDs.split(',').map(r => r.trim());
            allRIDs.push(...rids);
          }
        });

        setUserPendingRIDs(allRIDs); // Store RIDs in state for filtering
      } else {
        setUserPendingRIDs([]);
      }
    } catch (error) {
      console.error('Error parsing pending records:', error);
      setUserPendingRIDs([]);
    }
  }, [isOpen, activeData, currentUserID]);

  // Set agency context on apiService when connection changes
  useEffect(() => {
    if (isOpen && connection?.AgencyID) {
      apiService.setAgencyContext(connection.AgencyID);
    }
    return () => {
      apiService.clearAgencyContext();
    };
  }, [isOpen, connection?.AgencyID]);

  // Load records when dialog opens (connection/thread/task data comes from hooks)
  useEffect(() => {
    if (!isOpen || !connID || !connection) {
      setRecords([]);
      setTransactions([]);
      setContracts([]);
      setAccessUserIDs([]); // Reset access state
      setLocalStates(prev => ({ ...prev, loadingRecords: false }));
      return;
    }

    // Initialize access IDs from connection data
    if (connection.POCUserIDs) {
      const ids = connection.POCUserIDs.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      setAccessUserIDs(ids);
    }

    const loadData = async () => {
      try {
        setLocalStates(prev => ({ ...prev, loadingRecords: true }));
        const data = await getConnectionDetailAPI(connID, '');
        setFeedbackThreadDetail({
          thrTypeID: data?.rawData?.ThrTypeID || 0,
          thrReference: data?.rawData?.ThrReference || 0,
          thrProduct: data?.rawData?.ThrProduct || '',
          thrFeedbackDT: data?.rawData?.ThrFeedbackDT || null,
          thrFailureRsn: data?.rawData?.ThrFailureRsn || '',
        });
        setRecords(data?.records || []);
        setTransactions(data?.transactions || []);
        setContractsFromAPI(data?.contracts || []);
        setOrdersHistory(data?.ordersHistory.sort((a, b) => new Date(b.SinceDT) - new Date(a.SinceDT)) || []);
      } catch (error) {
        console.error("Error loading records:", error);
        setRecords([]);
        setTransactions([]);
        setOrdersHistory([]);
      } finally {
        setLocalStates(prev => ({ ...prev, loadingRecords: false }));
      }
    };
    loadData();
  }, [isOpen, connID, connection?.ConnID, dialogStates.threadEntry]);

  useEffect(() => {
    if (expandThreadID && isOpen && !hasExpandedRef.current) {
      const openInquiries = getThreadsData();
      let foundThread = openInquiries?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);

      // Check if there is only one active/open inquiry
      const isSingleActiveInquiry = foundThread && openInquiries?.length === 1;

      if (!foundThread) {
        foundThread = activeThreads?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);
      }

      if (!foundThread) {
        foundThread = allThreads?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);
      }

      if (!foundThread) {
        foundThread = ordersHistory?.find(t => t?.ThreadID === expandThreadID || t?.threadID === expandThreadID);
      }

      if (foundThread) {
        setSelectedThread(foundThread);
        // Only open popup details if it's NOT a single active inquiry (since single inquiry details are already displayed inside the card)
        if (!isSingleActiveInquiry) {
          setDialogStates((prev) => ({ ...prev, thrDetails: true }));
        }
        hasExpandedRef.current = true;
      }
    }
  }, [expandThreadID, isOpen, activeThreads, allThreads, ordersHistory]);

  // Merge contracts from Redux and API
  useEffect(() => {
    const reduxContracts = contractsFromRedux.filter(
      contract =>
        (contract.ConnID === connection?.ConnID || contract.ConnID === connID) &&
        contract.StatusID === 0
    );

    const mergedContracts = [...reduxContracts];

    // Remove duplicates based on ContractID
    const uniqueContracts = mergedContracts.filter(
      (contract, index, arr) =>
        arr.findIndex(c => c.ContractID === contract.ContractID) === index &&
        contract.StatusID === 0
    );

    setContracts(uniqueContracts);
  }, [contractsFromRedux, connection?.ConnID, connID]);

  // Update selectedThread when data changes (e.g. after edit)
  useEffect(() => {
    if (selectedThread && dialogStates.threadDetails) {
      // Try to find updated thread in active threads
      const updatedActive = activeThreads.find(t => t.ThreadID === selectedThread.ThreadID);
      if (updatedActive) {
        setSelectedThread(updatedActive);
        return;
      }
      // Try to find updated thread in orders history
      const updatedOrder = ordersHistory.find(o => o.ThreadID === selectedThread.ThreadID);
      if (updatedOrder) {
        setSelectedThread(updatedOrder);
        return;
      }
    }
  }, [activeThreads, ordersHistory, dialogStates.threadDetails]);

  // Reset scroll position when main dialog is closed
  useEffect(() => {
    if (!isOpen) {
      threadTableScrollPositionRef.current = 0;
    }
  }, [isOpen]);

  // Stable callback for thread updates
  const handleThreadUpdated = useCallback(async () => {
    if (!connection?.ConnID) return;
    try {
      setLocalStates(prev => ({ ...prev, loadingRecords: true }));
      const recordsData = await getConnectionDetailAPI(connID, '');
      setRecords(recordsData?.records || []);
      setTransactions(recordsData?.transactions || []);
      setOrdersHistory(recordsData?.ordersHistory.sort((a, b) => new Date(b.SinceDT) - new Date(a.SinceDT)) || []);
    } catch (error) {
      console.error("Error refreshing records and transactions:", error);
    } finally {
      setLocalStates(prev => ({ ...prev, loadingRecords: false }));
    }
  }, [connID, connection?.ConnID]);

  // Stable initial data object
  const threadInitialData = useMemo(() => {
    if (!dialogStates.isHistoryview || !selectedThread) {
      return null;
    }
    return {
      ...selectedThread,
      connectionData: {
        BizName: connection?.BizName,
        ContactName: connection?.ContactName,
        CC: connection?.CC,
        Mobile: connection?.Mobile,
        Email: connection?.Email,
        ConnType: connection?.ConnType
      }
    };
  }, [dialogStates.isHistoryview, selectedThread, connection?.BizName, connection?.ContactName, connection?.CC, connection?.Mobile, connection?.Email, connection?.ConnType]);


  // Get executive name by user ID
  const getExecutiveName = useCallback((pocUserID, badge = false) => {
    const user = users.find((user) => user.userID === pocUserID)
    if (user) return user.name;

    try {
      const allUsers = getStorageData('U') || [];
      const fallbackUser = allUsers.find(u => Number(u.UserID) === Number(pocUserID));
      if (fallbackUser) return fallbackUser.Name;
    } catch (e) {
      console.error('Error finding executive name in fallback:', e);
    }

    return badge ? "Not assigned" : "--"
  }, [users]);

  // Compute single thread data at component level
  const singleThreadData = useMemo(() => {
    const threads = getThreadsData();
    if (threads.length !== 1) return null;

    const singleThread = threads[0];

    return {
      threadID: singleThread.ThreadID,
      typeID: singleThread.TypeID,
      agencyID: singleThread.AgencyID,
      connID: singleThread.ConnID,
      mainProduct: singleThread.MainProduct,
      mainQty: singleThread.MainQty,
      model: singleThread.Model,
      pocUserID: singleThread.POCUserID,
      pocUserName: getExecutiveName(singleThread.POCUserID),
      statusID: singleThread.StatusID,
      dueDT: singleThread.DueDT,
      dueDTF: getDateWithoutTime(singleThread.DueDT),
      dueDTColor: getColorClass(getTaskColor(singleThread.DueDT)),
      sinceDT: singleThread.SinceDT,
      sinceDTF: getDateWithoutTime(singleThread.SinceDT),
      reference: singleThread.Reference,
      bizProspect: singleThread.BizProspect,
      currStageID: singleThread.CurrStageID,
      stageInfo: getStageInfoForDisplay(singleThread.TypeID, singleThread.CurrStageID),
      notes: singleThread.Notes,
      thrFile: singleThread.ThrFile || singleThread.thrFile || singleThread.thrfile,
      source: singleThread.Source,
      connectionData: {
        bizName: connection?.BizName,
        contactName: connection?.ContactName,
        cc: connection?.CC,
        mobile: connection?.Mobile,
        email: connection?.Email,
        ConnType: connection?.ConnType
      },
      feedbackNotes: singleThread.FdbkNotes,
      feedbackScore: singleThread.FdbkScore,
      failureReason: singleThread.FailureReason,
      statusHist: singleThread.StatusHist || singleThread.statusHist,
      //   customFields: singleThread?.CustomFields ? JSON.parse(singleThread?.CustomFields) : [],
      customFields: (singleThread?.ExtraFields && singleThread.ExtraFields !== "[]") ? JSON.parse(singleThread.ExtraFields) : (singleThread?.CustomFields && singleThread.CustomFields !== "[]" ? JSON.parse(singleThread.CustomFields) : []),
      model: singleThread.Model,
    };
  }, [activeThreads, connection, threadStages, threadTypesData, users, getExecutiveName]);

  if (!connection) return null

  const formatTaskData = (task) => {
    const dateObj = new Date(task.DT);
    return {
      taskID: task.TskID,
      connID: connection.ConnID,
      taskName: task.Ttl,
      description: task.Txt,
      dueDT: format(dateObj, 'yyyy-MM-dd'),
      dueTime: format(dateObj, 'HH:mm'),
      pocUserID: task.UID,
      priority: task.Pri,
      threads: task.TIDs ? task.TIDs.split(',') : '',
      status: task.Sts,
      taskType: task.Typ,
      repeatAfter: task.RepeatAfter || 0,
      repeatDT: task.RepeatDT || '',
    };
  };

  const openRepeatTaskEntry = (task) => {
    if (!task) return;
    setRepeatPrefillData({
      ...formatTaskData(task),
      taskID: 0,
      status: undefined,
      rmdrDT: undefined,
      dueDT: undefined,
      dueTime: undefined,
      connID: connection?.ConnID,
      connectionLocked: true,
      connectionLabel: 'Contact',
      bizName: connection?.BizName || '',
      contactName: connection?.ContactName || '',
      ctrcIDs: task.CIDs ? task.CIDs.split(",") : '',
      secConnIDs: task.SCIDs ? task.SCIDs.split(",").map(id => Number(id)).filter(Boolean) : '',
    });
    setTaskEntryMode('repeat');
    setDialogStates(prev => ({ ...prev, taskEntry: true }));
  };

  const refreshConnectionData = async () => {
    await handleThreadUpdated();
  }

  const refreshRecordsAndTransactions = async () => {
    if (!connection?.ConnID) return

    try {
      setLocalStates(prev => ({ ...prev, loadingRecords: true }));
      const recordsData = await getConnectionDetailAPI(connID, '');
      setRecords(recordsData?.records || []);
      setTransactions(recordsData?.transactions || []);
      setOrdersHistory(recordsData?.ordersHistory.sort((a, b) => new Date(b.SinceDT) - new Date(a.SinceDT)) || []); // Also update orders
    } catch (error) {
      console.error("Error refreshing records and transactions:", error);
    } finally {
      setLocalStates(prev => ({ ...prev, loadingRecords: false }));
    }
  }

  // Unified handler for all item actions
  const handleItemAction = (action, item, type, event = null) => {
    if (event) event.stopPropagation(); // Handle event propagation for actions like edit

    switch (action) {
      case 'THRDTL': {
        setSelectedThread(item)
        setDialogStates((prev) => ({ ...prev, thrDetails: true }))
      }
      case 'SELECT':
        if (type === 'thread') {
          setSelectedThread(item);
          setThreadDetailDialog(true);
        } else if (type === 'task') {
          setSelectedTask(item);
          setTaskDetailDialog(true);
        } else if (type === 'contract') {
          setSelectedContractForDetail(item);
          setContractDetailDialog(true);
        }
        break;
      case 'COMPLETE':
        if (type === 'thread') {
          setSelectedThreadForAction(item);
          setDialogStates(prev => ({ ...prev, confirmThreadCompleteAlert: true }));
        } else if (type === 'task') {
          // Tasks embedded in connection.Tasks don't carry ConnID/BizName/ContactName of their own -
          // they're implicitly scoped to this connection, so stamp those on for the complete dialog's
          // "has a linked connection" check (Yes, Add Record / No, Just Complete).
          setSelectedTaskForRecord({
            ...item,
            ConnID: item.ConnID ?? connection?.ConnID,
            BizName: item.BizName ?? connection?.BizName,
            ContactName: item.ContactName ?? connection?.ContactName,
          });
          setDialogStates(prev => ({ ...prev, confirmCompleteAlert: true }));
        }
        break;
      case 'CANCEL':
        if (type === 'thread') {
          setSelectedThreadForAction(item);
          setDialogStates(prev => ({ ...prev, confirmThreadCancelAlert: true }));
        } else if (type === 'task') {
          setSelectedTaskForRecord(item);
          setDialogStates(prev => ({ ...prev, confirmCancelAlert: true }));
        }
        break;
      case 'EDIT':
        if (type === 'thread') {
          setEditingThreadID(item.ThreadID || item.threadID);
          setEditingThreadTypeID(item.TypeID || item.typeID);
          setDialogStates(prev => ({ ...prev, threadEntry: true, prefilledData: item }));
        } else if (type === 'task') {
          setEditingTaskID(item.TskID);
          setDialogStates(prev => ({ ...prev, taskEntry: true }));
        } else if (type === 'transaction') {
          // Handle transaction edit
          const invoiceDate = item.XnDate ? new Date(item.XnDate) : null;
          const dueDate = item.DueDate ? new Date(item.DueDate) : null;
          const prefilledData = {
            connection: item.ConnID,
            contactPerson: item.IsSeller ? item.BuyerUserName : item.SellerUserName,
            invoiceNo: item.XnNo,
            referenceNo: item.ConnRefNo,
            xnDate: invoiceDate ? format(invoiceDate, "yyyy-MM-dd") : "",
            dueDate: dueDate ? format(dueDate, "yyyy-MM-dd") : "",
            notes: item.IntNotes,
            bizName: item.IsSeller ? item.BuyerAgencyName : item.SellerAgencyName,
            contactName: item.IsSeller ? item.BuyerUserName : item.SellerUserName,
            mobile: item.IsSeller ? item.BuyerMobile : item.SellerMobile,
            email: item.IsSeller ? item.BuyerEmail : item.SellerEmail,
            cc: item.IsSeller ? item.BuyerCC : item.SellerCC,
            fileName: item.XnFile || "",
            preTaxAmount: item.AmtSubTotal,
            taxAmount: item.AmtTaxTotal,
            totalAmount: item.AmtGrandTotal
          };

          setSelectedTransactionType(item.XnTypeID);
          setSelectedTransaction(item);
          setTransactionPrefilledData(prefilledData);
          setDialogStates((prev) => ({ ...prev, transactionEntry: true }));
        } else if (type === 'contract') {
          // Handle contract edit
          const startDate = item.StartDT ? new Date(item.StartDT) : null;
          const expireDate = item.ExpiryDT ? new Date(item.ExpiryDT) : null;
          const contractType = item.IsSeller ? 'sold' : 'purchase';

          const prefilledData = {
            connection: item.ConnID,
            contactPerson: item.ContactName,
            contractNo: item.ContractNo,
            contractType: item.ContractType,
            contractTypeSelection: contractType,
            product: item.Product,
            startDate: startDate ? format(startDate, "yyyy-MM-dd") : "",
            expireDate: expireDate ? format(expireDate, "yyyy-MM-dd") : "",
            charges: item.ContractCharges,
            renewal: item.RenewalCharges,
            notes: item.Notes,
            bizName: connection?.BizName || "",
            contactName: item.ContactName,
            fileName: item.Attachment || ""
          };

          setSelectedContractType(contractType);
          setSelectedContract(item);
          setContractPrefilledData(prefilledData);
          setDialogStates((prev) => ({ ...prev, contractEntry: true }));
        }
        break;
      case 'DOWNLOAD':
        if (type === 'transaction' && item.XnFile) {
          const fileUrl = `/Uploads/${item.XnFile}`;
          window.open(fileUrl, "_blank");
        } else if (type === 'contract' && item.ContractFile) {
          const fileUrl = `/Uploads/${item.ContractFile}`;
          window.open(fileUrl, "_blank");
        }
        break;
      case 'RECORD_AND_COMPLETE':
        if (type === 'task') {
          setDialogStates(prev => ({ ...prev, confirmCompleteAlert: false, recordEntry: true }));
        }
        break;
      case 'INQUIRY_ONLY_MODE_EDIT':
        if (type === 'thread') {
          setDialogStates(prev => ({
            ...prev, threadEntry: true, isInquiryOnlyMode: true, prefilledData: {
              connID: item?.ConnID,
              contactPerson: item?.connectionData?.contactName || item?.ContactPerson,
              product: item?.product || item?.MainProduct,
              model: item?.Model,
              reference: item?.Reference,
              quantity: item?.MainQty,
              businessValue: item?.BizProspect,
              notes: item?.Notes,
              source: item?.Source,
              inquiryThreadID: item?.ThreadID,
              ...(item?.SinceDT && { sinceDT: item.SinceDT }),
            }
          }));
        }
      default:
        console.warn(`Unknown action: ${action}`);
        break;
    }
  };

  // Generic handler for thread/task actions
  const handleAction = async (item, action, type, shareByEmail = false, takeFeedback = false, rejReason = '', isMarkCold = false, shareByWhatsApp = false) => {
    const id = type === 'thread' ? item.ThreadID : item.TskID;
    const loadingKey = type === 'thread' ? 'completing' : (action === 'DONE' ? 'completing' : 'cancelling');

    const setLoading = type === 'thread' ? setThreadActionLoading : setTaskActionLoading;

    setLoading(prev => ({ ...prev, [id]: loadingKey }));

    try {
      if (type === 'thread') {
        const threadData = {
          threadID: id,
          typeID: item.TypeID || item.typeID,
          pocUserID: item.POCUserID,
          doneNotes: `${action === 'DONE' ? 'Thread completed' : rejReason}`,
          shareByEmail,
          shareByWhatsApp,
          takeFeedback,
          connID: item?.ConnID,
          currencySymbol: currencySymbol,
          reference: item?.Reference,
          bizProspect: item?.BizProspect,
          mainProduct: item?.MainProduct,
          isCold: isMarkCold,
          connectionData: {
            bizName: item?.connectionData?.BizName || connection?.BizName,
            contactName: item?.connectionData?.ContactName || connection?.ContactName,
            email: item?.connectionData?.Email || connection?.Email,
            mobile: item?.connectionData?.Mobile || connection?.Mobile,
            cc: item?.connectionData?.CC || connection?.CC
          }
        };
        if (action === 'CANCEL') {
        }
        const result = await performThreadActionAPI(threadData, action);
        if (result.error == false && result.message == "Success") {
          toast.success(`${getThreadNameFromTypeID(selectedThreadForAction?.TypeID, threadTypesData, "SnSng")} ${action === 'DONE' ? 'marked as done.' : 'cancelled.'}`);
          if (action === 'DONE') {
            const isSale = [2, 5].includes(Number(item.TypeID || item.typeID));
            if (isSale) {
              setCompletedThreadForAttachment(item);
              setAttachDocDialog(true);
            }
          }
        }
      } else {
        const taskData = {
          taskID: id,
          connID: connection.ConnID,
          pocUserID: item.UID,
          taskName: item.Ttl,
          description: item.Txt,
          priority: item.Pri,
          status: item.Sts,
          taskType: item.Typ,
          threads: item.TIDs,
          AgencyID: item.AgencyID || connection?.AgencyID,
        };

        if (action === 'DONE') {
          await completeTaskAPI(taskData);
          // playTaskSuccessSound();
          toast.success('Task completed.');
        } else {
          // Assignee (not creator) declining the task is a Reject, not a Cancel
          const myUserIDs = getMyAgenciesMappedIDs('UserID');
          const taskCreatorID = Number(item.CreatedUID || item.UID || 0);
          const isMyCreator = myUserIDs.includes(taskCreatorID);
          const isMyAssignee = myUserIDs.includes(Number(item.UID)) || Number(item.UID) === Number(currentUserID);
          const isReject = isMyAssignee && !isMyCreator;

          if (isReject) {
            await rejectTaskAPI(taskData);
          } else {
            await cancelTaskAPI(taskData);
          }
          toast.success(isReject ? 'Task rejected.' : 'Task cancelled.');
        }
      }

      if (refreshRecordsAndTransactions) {
        await refreshRecordsAndTransactions();
      }
    } catch (error) {
      console.error(`Error ${action === 'DONE' ? 'completing' : 'cancelling'} ${type}:`, error);
      toast.error(error.message || `Failed to ${action === 'DONE' ? 'complete' : 'cancel'} ${type}. Please try again.`);
    } finally {
      setLoading(prev => ({ ...prev, [id]: null }));
      setDialogStates(prev => ({
        ...prev,
        confirmThreadCompleteAlert: false,
        confirmThreadCancelAlert: false,
        confirmCompleteAlert: false,
        confirmCancelAlert: false
      }));
      setSelectedThreadForAction(null);
      setSelectedTaskForRecord(null);
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!file || !completedThreadForAttachment) return false;

    if (file.size > 1024 * 1024) {
      toast.error("File size must be up to 1 MB only.");
      return false;
    }

    try {
      dispatch(setGlobalLoading(true));
      const uploadResult = await apiService.uploadFile(file, "Threads", "both");

      if (uploadResult?.error) {
        toast.error(uploadResult?.message || "Failed to upload file");
        return false;
      }

      const uploadedFileName = uploadResult?.data?.relativePath;
      if (!uploadedFileName) {
        toast.error("Failed to get uploaded file path");
        return false;
      }

      const threadTypes = masterData?.threadTypes || threadTypesData || [];
      const targetData = {
        ...completedThreadForAttachment,
        threadID: completedThreadForAttachment.ThreadID || completedThreadForAttachment.threadID,
        typeID: completedThreadForAttachment.TypeID || completedThreadForAttachment.typeID,
        pocUserID: completedThreadForAttachment.POCUserID || completedThreadForAttachment.pocUserID,
        connID: completedThreadForAttachment.ConnID || completedThreadForAttachment.connID,
        thrFile: uploadedFileName
      };

      const result = await manageThreadAPI(targetData, 'EDIT', threadTypes);

      if (result?.error === false && result?.message === 'Success') {
        toast.success("Document attached successfully.");
        if (refreshRecordsAndTransactions) {
          await refreshRecordsAndTransactions();
        }
        setAttachDocDialog(false);
        setCompletedThreadForAttachment(null);
        return true;
      } else {
        toast.error(result?.message || "Failed to save attachment to sale record.");
        return false;
      }
    } catch (err) {
      console.error("Error uploading attachment for completed sale:", err);
      toast.error("Failed to upload document.");
      return false;
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Threads table component with actions
  const ThreadsTable = () => {
    const threads = getThreadsData();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [hasScrollbar, setHasScrollbar] = useState(false);

    // Check if any thread has a reference
    const hasAnyThreadReference = useMemo(() => {
      return threads.some(thread => thread.Reference && thread.Reference !== "0" && thread.Reference !== 0);
    }, [threads]);

    useEffect(() => {
      const checkScrollbar = () => {
        if (threadScrollableRef.current) {
          const hasVerticalScrollbar = threadScrollableRef.current.scrollHeight > threadScrollableRef.current.clientHeight;
          setHasScrollbar(hasVerticalScrollbar);
        }
      };

      checkScrollbar();
      const timeoutID = setTimeout(checkScrollbar, 100);
      return () => clearTimeout(timeoutID);
    }, [threads]);

    // Effect to restore and maintain scroll position
    useEffect(() => {
      if (threadScrollableRef.current && threadTableScrollPositionRef.current > 0) {
        threadScrollableRef.current.scrollTop = threadTableScrollPositionRef.current;
      }
    });

    const handleThreadTypeSelect = (threadTypeID) => {
      setLocalStates((prev) => ({ ...prev, selectedThreadType: threadTypeID }));
      setDialogStates((prev) => ({ ...prev, threadEntry: true }));
      setDropdownOpen(false);
    };

    const getStageInfo = (currStageID) => {
      return stageMap[currStageID] || {
        name: "Active",
        bg: "bg-sahi-badge-bg-color",
        text: connDetailsClasses.textColor,
      };
    };

    // If only one thread, show thread details card instead of table
    if (threads.length === 1 && singleThreadData) {
      const threadTypeMeta = getThreadTypeMeta(singleThreadData?.typeID, threadTypesData);
      const canShowReassignButton = users.length > 1;
      const transformedThreadData = singleThreadData;

      const handleReassign = () => {
        setSelectedThreadForAction(transformedThreadData);
        setDialogStates(prev => ({ ...prev, reassignDialog: true }));
      };

      const handleUpdateStatus = async (stageID, markAsDone = false, shareByEmail = false, takeFeedback = false, completionNote = '') => {
        try {
          dispatch(setGlobalLoading(true));
          const thrdApiData = {
            ...transformedThreadData,
            shareByEmail,
            takeFeedback,
            doneNotes: completionNote,
            currencySymbol: currencySymbol
          };
          const result = markAsDone
            ? await performThreadActionAPI(thrdApiData, 'DONE')
            : await updateThreadStageAPI(thrdApiData, stageID, markAsDone);

          if (result.error === false && result.message === 'Success') {
            toast.success(markAsDone
              ? `${getThreadNameFromTypeID(transformedThreadData?.typeID, threadTypesData, "SnSng")} marked as done.`
              : `${getThreadNameFromTypeID(transformedThreadData?.typeID, threadTypesData, "SnSng")} status updated.`
            );
            handleThreadUpdated();
          }
        } catch (error) {
          console.error('Error updating thread status:', error);
          toast.error('Failed to update thread status');
        } finally {
          dispatch(setGlobalLoading(false));
        }
      };

      //   const handleCloseThread = async (rejReason) => {
      //     setSelectedThreadForAction(transformedThreadData);
      //     setDialogStates(prev => ({ ...prev, confirmThreadCancelAlert: true }));
      //   };


      const handleCloseThread = async (currentThreadData, rejReason, isMarkCold = false) => {
        try {
          dispatch(setGlobalLoading(true));
          const result = await performThreadActionAPI({
            ...currentThreadData,
            doneNotes: rejReason ? rejReason : 'Thread Cancelled',
            isCold: isMarkCold
          }, 'CANCEL');

          if (result.error === false) {
            toast.success(`${getThreadNameFromTypeID(currentThreadData?.typeID, threadTypesData, "SnSng")} ${currentThreadData.typeID === 4 ? 'marked as fake' :
              currentThreadData.typeID === 1 ? 'rejected' : 'cancelled'
              }.`);
          }
        } catch (error) {
          console.error('Error while cancelling thread', error);
        } finally {
          dispatch(setGlobalLoading(false));
          onClose();
        }
      };

      const handleConvertInquiry = () => {
        setSelectedThreadForAction(transformedThreadData);
        setEditingThreadID(null);
        setEditingThreadTypeID(2);
        setDialogStates(prev => ({ ...prev, convertToSales: true }));
      };

      const handleEditThread = () => {
        setEditingThreadID(transformedThreadData.threadID);
        setEditingThreadTypeID(transformedThreadData.typeID);
        setDialogStates(prev => ({ ...prev, threadEntry: true, prefilledData: transformedThreadData }));
      };

      //   const handleMarkFake = async (threadData) => {
      //     setSelectedThreadForAction(threadData);
      //     setDialogStates(prev => ({ ...prev, confirmThreadCancelAlert: true }));
      //   };

      const handleMarkFake = async (threadData, reasonText = '', isMarkCold = false) => {
        try {
          dispatch(setGlobalLoading(true));
          const threadName = getThreadNameFromTypeID(threadData?.typeID, threadTypesData, "SnSng");
          const isInquiry = threadData?.typeID === 1 || threadData?.typeID === 4;
          const finalReasonText = reasonText || `Marked as Fake ${threadName}`;

          const result = await performThreadActionAPI({
            ...threadData,
            doneNotes: finalReasonText,
            isCold: isMarkCold
          }, 'CANCEL');

          if (result.error === false) {
            if (isInquiry) {
              toast.success(`${threadName} marked as fake.`);
            } else {
              toast.success(`${threadName} cancelled.`);
            }
          }
        } catch (error) {
          console.error(`Error while cancelling ${getThreadNameFromTypeID(threadData?.typeID, threadTypesData, "SnSng")}`, error);
          toast.error(`Failed to cancel ${getThreadNameFromTypeID(threadData?.typeID, threadTypesData, "SnSng")}`);
        } finally {
          dispatch(setGlobalLoading(false));
          onClose();
        }
      };

      return (
        <div className="space-y-3">
          <div className={connDetailsClasses.flexBetween}>
            <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>
              {`Open ${currActiveThreadType.typPlr} (${threads.length})`}
            </h3>
            <div className="flex items-center gap-2">
              {isOwnAgencyConnection && canAddThr && <ThreadDropdown
                onThreadTypeSelect={handleThreadTypeSelect}
                iconButton={true}
                connectionType={connection?.ConnType}
                externalOpen={dropdownOpen}
                onExternalOpenChange={setDropdownOpen}
                isSmallBtn={true}
                threadTypeIDs={isInqOnlyMode ? [1, 2] : []}
                excludeThreadTypeIDs={allOrders.length > 0 ? [2] : []}
              />}
              {isOwnAgencyConnection && <Button
                variant="secondary"
                className="px-3"
                onClick={() => {
                  onClose()
                  window.open(`threads-report?tID=1&cid=${connection?.ConnID}`, '_blank')
                }}
              >
                <History className="h-4 w-4" />
              </Button>}
            </div>
          </div>
          <Card className="bg-white overflow-hidden !gap-0">
            <div className="flex items-center justify-between gap-2 px-3">
              <div className="flex-1 min-w-0">
                {(() => {
                  const parts = isInqOnlyMode ? ['Inquiry'] : ['Thread'];

                  if (transformedThreadData?.reference) {
                    parts.push(`#${transformedThreadData.reference}`);
                  }

                  if (transformedThreadData?.mainProduct) {
                    parts.push(`for ${transformedThreadData.mainProduct} ${transformedThreadData.model ? `(${transformedThreadData.model || ''})` : ''}`);
                  }

                  const titleText = parts.join(' ');

                  return (
                    <h4 className="text-xs md:text-sm font-semibold text-color table-header-text inline-flex items-center">
                      <span className="truncate">
                        {titleText}
                        {transformedThreadData?.bizProspect > 0 && (
                          <>
                            {' ('}
                            <FormatAmount amount={transformedThreadData.bizProspect} currencySymbol={currencySymbol} />
                            {')'}
                          </>
                        )}
                      </span>
                    </h4>
                  );
                })()}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {canShowEditButton && transformedThreadData?.currStageID !== 7 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditThread}
                    className="h-7 w-7 p-0 text-sahi-blue flex-shrink-0"
                    title={`Edit ${getThreadNameFromTypeID(threads[0]?.TypeID, threadTypesData, "SnSng") || "Thread"}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <ThrDetailsInfo
              threadData={transformedThreadData}
              threadTypeMeta={threadTypeMeta}
              onReassign={isOwnAgencyConnection ? handleReassign : undefined}
              canShowAssignedTo={isOwnAgencyConnection && canShowReassignButton}
            />
            <FeedbackSection
              feedbackScore={transformedThreadData?.feedbackScore}
              feedbackNotes={transformedThreadData?.feedbackNotes}
              failureReason={transformedThreadData?.failureReason}
            />
            <CardContent className="!px-0 !pb-2">
              <ThrDetailsStatus
                threadData={transformedThreadData}
                threadTypeMeta={threadTypeMeta}
                onStageUpdate={isOwnAgencyConnection ? handleUpdateStatus : undefined}
              />

              {isOwnAgencyConnection &&
                <div className="p-4 pt-0">
                  <ThrDetailsActions
                    threadData={transformedThreadData}
                    onUpdateStatus={handleUpdateStatus}
                    onCloseThread={(rejReason, isMarkCold) => handleCloseThread(transformedThreadData, rejReason, isMarkCold)}
                    onConvertInquiry={handleConvertInquiry}
                    onMarkFake={(threadData, reasonText, isMarkCold) => handleMarkFake(threadData, reasonText, isMarkCold)}
                    canShowReassignButton={canShowReassignButton}
                  />
                </div>}
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className={connDetailsClasses.flexBetween}>
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>
            {threads.length > 0 ? `Open ${currActiveThreadType.typPlr} (${threads.length})` : `Open ${currActiveThreadType.typPlr}`}
          </h3>
          <div className="flex items-center gap-2">
            {isOwnAgencyConnection && canAddThr && <ThreadDropdown
              onThreadTypeSelect={handleThreadTypeSelect}
              iconButton={true}
              connectionType={connection?.ConnType}
              externalOpen={dropdownOpen}
              onExternalOpenChange={setDropdownOpen}
              isSmallBtn={true}
              threadTypeIDs={isInqOnlyMode ? [1, 2] : []}
              excludeThreadTypeIDs={allOrders.length > 0 ? [2] : []}
            />}
            {isOwnAgencyConnection && <Button
              variant="secondary"
              className="px-3"
              onClick={() => {
                onClose()
                window.open(`threads-report?tID=1&cid=${connection?.ConnID}`, '_blank')
              }
              }
            >
              <History className="h-4 w-4" />
            </Button>}
          </div>
        </div>

        {threads.length > 0 ? (
          <Card className="bg-white overflow-hidden lg:!pt-0">
            <CardContent className="p-0 relative">
              {/* Container for both header and body to maintain alignment */}
              <div className="table-container overflow-x-auto">
                {/* Fixed header */}
                <div
                  className="bg-white border-b border-gray-200 overflow-hidden"
                  style={{
                    marginRight: hasScrollbar ? '8px' : '0px',
                    transition: 'margin-right 0.1s ease'
                  }}
                >
                  <Table className="w-full table-fixed">
                    <colgroup className="threads-table-cols">
                      {getActiveThreadTypesArr.length > 0 && <col className="hidden lg:table-column" style={{ width: '14%' }} />}
                      {hasAnyThreadReference && <col style={{ width: '10%' }} />}
                      <col style={{ width: '18%' }} />
                      <col className="hidden md:table-column" style={{ width: '12%' }} />
                      <col style={{ width: '11%' }} />
                      <col className="hidden xl:table-column" style={{ width: '11%' }} />
                      <col className="hidden lg:table-column" style={{ width: '14%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableHeader className="!border-0">
                      <TableRow className="bg-white !border-0">
                        {getActiveThreadTypesArr.length > 0 && <TableHead className={`hidden lg:table-cell table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Type</TableHead>}
                        {hasAnyThreadReference && <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Ref No.</TableHead>}
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Offering</TableHead>
                        <TableHead className={`hidden md:table-cell table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-right`}>Amount ({currencySymbol})</TableHead>
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Expected by</TableHead>
                        <TableHead className={`hidden xl:table-cell table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Stage</TableHead>
                        <TableHead className={`hidden lg:table-cell table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Notes</TableHead>
                        <TableHead className="table-header-text whitespace-nowrap px-3 py-2 text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </div>

                {/* Scrollable body */}
                <div
                  ref={threadScrollableRef}
                  className="table-scrollable-body !max-h-[150px] !overflow-y-auto"
                  onScroll={(e) => {
                    threadTableScrollPositionRef.current = e.target.scrollTop;
                  }}
                >
                  <Table className="w-full table-fixed">
                    <colgroup className="threads-table-cols">
                      {getActiveThreadTypesArr.length > 0 && <col className="hidden lg:table-column" style={{ width: '14%' }} />}
                      {hasAnyThreadReference && <col style={{ width: '10%' }} />}
                      <col style={{ width: '18%' }} />
                      <col className="hidden md:table-column" style={{ width: '12%' }} />
                      <col style={{ width: '11%' }} />
                      <col className="hidden xl:table-column" style={{ width: '11%' }} />
                      <col className="hidden lg:table-column" style={{ width: '14%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableBody className="!border-0">
                      {threads.map((thread, index) => {
                        const threadTypeMeta = getThreadTypeMeta(thread.TypeID || thread.typeID, threadTypesData);
                        const stageInfo = getStageInfo(thread.CurrStageID || thread.currStageID || thread.StatusID);
                        return (
                          <TableRow
                            key={thread.ThreadID || index}
                            className={`${connDetailsClasses.hoverBg} border-gray-100`}
                            onClick={(e) => handleItemAction('THRDTL', thread, 'thread', e)}
                          >
                            {getActiveThreadTypesArr.length > 0 && <TableCell className={`hidden lg:table-cell px-3 py-2 text-sm text-left`}>
                              {threadTypeMeta ? (
                                <div className={`${threadTypeMeta.colorClass} badge-text flex justify-start items-start mt-0`}>
                                  <threadTypeMeta.Icon className="mr-2 h-3 w-3 shrink-0" />
                                  <span className={`${connDetailsClasses.truncate} text-sm !text-foreground leading-none`}>{threadTypeMeta.nameSng}</span>
                                </div>
                              ) : (
                                <Badge variant="outline" className={`text-xs border-sahi-border-color ${connDetailsClasses.textSubColor} mt-0`}>
                                  {thread.TypeName || thread.Type || "General"}
                                </Badge>
                              )}
                            </TableCell>}
                            {hasAnyThreadReference && <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              <div className={connDetailsClasses.truncate}>
                                {thread.Reference && thread.Reference !== "0" && thread.Reference !== 0 ? thread.Reference : "--"}
                              </div>
                            </TableCell>}
                            <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              <div className={connDetailsClasses.truncate} title={thread.MainProduct || thread.product || thread.Product || "--"}>
                                {thread.MainProduct || thread.product || thread.Product || "--"}
                              </div>
                            </TableCell>
                            <TableCell className={`hidden md:table-cell ${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-right tabular-nums`}>
                              <div className={connDetailsClasses.truncate}>
                                {thread.Amount || thread.BizProspect || thread.bizProspect
                                  ? `${(thread.Amount || thread.BizProspect || thread.bizProspect).toLocaleString("en-US")}`
                                  : "--"}
                              </div>
                            </TableCell>
                            <TableCell className={`${connDetailsClasses.tableCell} text-left`}>
                              <div className={`text-sm ${getColorClass(getTaskColor(thread.DueDT))} whitespace-nowrap`}>
                                {thread.DueDT ? getDateWithoutTime(thread.DueDT) : "--"}
                              </div>
                            </TableCell>
                            <TableCell className={`hidden xl:table-cell ${connDetailsClasses.tableCell} text-left`}>
                              <div className={connDetailsClasses.truncate}>
                                <span>{stageInfo.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className={`hidden lg:table-cell ${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              {thread.Notes && thread.Notes !== "--" ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={connDetailsClasses.truncate}>
                                      {thread.Notes}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p>{thread.Notes}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <div className={connDetailsClasses.truncate}>--</div>
                              )}
                            </TableCell>
                            <TableCell className={`${connDetailsClasses.tableCell} text-right`}>
                              {isOwnAgencyConnection && (thread.ConnID == connection.ConnID) && hasAgencyAccess && <div className="flex items-center justify-end gap-1 xl:gap-2">
                                {/* Complete Action */}
                                {(thread.StatusID !== 2 && thread.StatusID !== 3) ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={connDetailsClasses.trueActionButton}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const threadName = getThreadNameFromTypeID(thread?.TypeID, threadTypesData, "SnSng");
                                          const threadNameLower = threadName?.toLowerCase();

                                          if (isPendingTasks(thread?.ThreadID,
                                            `Cannot complete ${threadName}`,
                                            `Please complete tasks before completing this ${threadNameLower}.`)
                                          ) {
                                            return;
                                          }
                                          if (!threadActionLoading[thread.ThreadID] && !dialogStates.isInquiryOnlyMode) {
                                            handleItemAction('COMPLETE', thread, 'thread', e);
                                          }
                                          else if (isInqOnlyMode) {
                                            handleItemAction('INQUIRY_ONLY_MODE_EDIT', thread, 'thread', e);
                                            setThrEntryMode('INQDONE')
                                            setLocalStates((prev) => ({
                                              ...prev,
                                              selectedThreadType: 2,
                                            }));
                                          }
                                        }}
                                        disabled={threadActionLoading[thread.ThreadID]}
                                      >
                                        {threadActionLoading[thread.ThreadID] === 'completing' ? (
                                          <div className={`h-3 w-3 ${connDetailsClasses.loadingSpinner} border-sahi-green border-t-transparent`} />
                                        ) : (
                                          <Check className="h-3 w-3 text-sahi-green" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{dialogStates.isInquiryOnlyMode ? `Convert to Sale` : `Mark as complete`}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-text-color h-6 w-6 flex items-center justify-center leading-none"></span>
                                )}

                                {/* Cancel Action */}
                                {thread.StatusID !== 3 ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={connDetailsClasses.falseActionButton}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isPendingTasks(
                                            thread?.ThreadID,
                                            'Cannot convert inquiry.',
                                            'Please complete tasks before converting this inquiry.'
                                          )) {
                                            return;
                                          }
                                          if (!threadActionLoading[thread.ThreadID]) {
                                            handleItemAction('CANCEL', thread, 'thread', e);
                                          }
                                        }}
                                        disabled={threadActionLoading[thread.ThreadID]}
                                      >
                                        {threadActionLoading[thread.ThreadID] === 'cancelling' ? (
                                          <div className={`h-3 w-3 ${connDetailsClasses.loadingSpinner} border-sahi-red border-t-transparent`} />
                                        ) : (
                                          <X className="h-3 w-3 text-sahi-red" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Cancel {getThreadNameFromTypeID(thread?.TypeID, threadTypesData, "SnSng")?.toLowerCase() || 'thread'}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-text-color h-6 w-6 flex items-center justify-center leading-none"></span>
                                )}

                                {canShowEditButton &&
                                  <>
                                    {/* Edit Action - Always available */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className={connDetailsClasses.editActionButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleItemAction('EDIT', thread, 'thread', e)
                                            if (dialogStates.isInquiryOnlyMode) {
                                              setLocalStates((prev) => ({
                                                ...prev,
                                                selectedThreadType: 1,
                                              }));
                                            }
                                          }}
                                        >
                                          <Pencil className="h-3 w-3 text-sahi-blue" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Edit {getThreadNameFromTypeID(thread?.TypeID, threadTypesData, "SnSng") || 'Thread'}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </>
                                }
                              </div>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <NoDataCard
            Icon={ClipboardList}
            title={`No ${currActiveThreadType.typPlrLower} found.`}
            desc={`Click to add  ${isInqOnlyMode ? 'an' : 'a'} ${currActiveThreadType.typSngLower}.`}
            onClick={() => { setDialogStates((prev) => ({ ...prev, threadEntry: true })) }}
          />
        )}
      </div>
    );
  };

  const OrdersHistoryCard = () => {
    const orders = allOrders;
    const [hasScrollbar, setHasScrollbar] = useState(false);

    // Check if any order has a reference
    const hasAnyOrderReference = useMemo(() => {
      return orders.some(order => order.Reference && order.Reference !== "0" && order.Reference !== 0);
    }, [orders]);

    if (orders.length === 0) {
      return null;
    }

    const handleOrderTypeSelect = () => {
      setLocalStates((prev) => ({ ...prev, selectedThreadType: 2 }));
      setDialogStates((prev) => ({ ...prev, threadEntry: true }));
    };

    const handleScrollbarCheck = (ref) => {
      if (ref) {
        const hasVerticalScroll = ref.scrollHeight > ref.clientHeight;
        setHasScrollbar(hasVerticalScroll);
      }
    };

    return (
      <div className="space-y-3">
        <div className={connDetailsClasses.flexBetween}>
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>
            {orders.length > 0 ? `Recent Sales (${orders.length})` : `Recent Sales`}
          </h3>
          <div className="flex items-center gap-2">
            {isOwnAgencyConnection && <Button onClick={() => {
              setThrEntryMode('OPEN_ORD');
              setLocalStates((prev) => ({ ...prev, selectedThreadType: 2 }));
              setDialogStates((prev) => ({ ...prev, threadEntry: true }));
            }}>
              <Plus className="h-4 w-4" />
              Enter Sale
            </Button>}
            {isOwnAgencyConnection && <Button
              variant="secondary"
              className="px-3"
              onClick={() => {
                onClose()
                window.open(`threads-report?tID=2&p=This%20Year&cid=${connection?.ConnID}`, '_blank')
              }}
            >
              <History className="h-4 w-4" />
            </Button>}
          </div>
        </div>

        {orders.length > 0 && (
          <Card className="bg-white overflow-hidden lg:!pt-0">
            <CardContent className="p-0 relative">
              <div className="table-container overflow-x-auto">
                {/* Fixed header */}
                <div
                  className="bg-white border-b border-gray-200 overflow-hidden"
                  style={{
                    marginRight: hasScrollbar ? '8px' : '0px',
                    transition: 'margin-right 0.1s ease'
                  }}
                >
                  <Table className="w-full table-fixed">
                    <colgroup className="threads-table-cols">
                      {hasAnyOrderReference && <col style={{ width: '8%' }} />}
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '9%' }} />
                      <col className="hidden md:table-column" style={{ width: '11%' }} />
                      <col className="hidden xl:table-column" style={{ width: '18%' }} />
                      <col className="hidden lg:table-column" style={{ width: '31%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableHeader className="!border-0">
                      <TableRow className="bg-white !border-0">
                        {hasAnyOrderReference && <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Ref No.</TableHead>}
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Offering</TableHead>
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Received on</TableHead>
                        <TableHead className={`hidden md:table-cell table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-right`}>Amount ({currencySymbol})</TableHead>
                        <TableHead className={`hidden xl:table-cell table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Status</TableHead>
                        <TableHead className={`hidden lg:table-cell table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Notes</TableHead>
                        <TableHead className="table-header-text whitespace-nowrap px-3 py-2 text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </div>

                {/* Scrollable body */}
                <div
                  className="table-scrollable-body !max-h-[150px] !overflow-y-auto"
                  onScroll={(e) => handleScrollbarCheck(e.target)}
                >
                  <Table className="w-full table-fixed">
                    <colgroup className="threads-table-cols">
                      {hasAnyOrderReference && <col style={{ width: '8%' }} />}
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '9%' }} />
                      <col className="hidden md:table-column" style={{ width: '11%' }} />
                      <col className="hidden xl:table-column" style={{ width: '18%' }} />
                      <col className="hidden lg:table-column" style={{ width: '31%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableBody className="!border-0">
                      {orders.map((order, index) => {
                        const isOpenOrder = Number(order.StatusID || order.statusID || order.Sts || 0) !== 2 && Number(order.StatusID || order.statusID || order.Sts || 0) !== 3;
                        const stageInfo = getStageInfo(order.CurrStageID || order.currStageID || order.StatusID);

                        let orderStatusText = '';
                        if (isOpenOrder) {
                          try {
                            const rawHist = order.StatusHist || order.statusHist;
                            if (rawHist) {
                              const hist = typeof rawHist === 'string' ? JSON.parse(rawHist) : rawHist;
                              if (Array.isArray(hist) && hist.length > 0) {
                                const last = hist[hist.length - 1];
                                orderStatusText = last?.S || last?.Status || '';
                              }
                            }
                          } catch (e) {
                            console.error('Error parsing order StatusHist:', e);
                          }
                          if (!orderStatusText) {
                            orderStatusText = order.CurrStage || order.currStage || order.Status || order.status || stageInfo?.name || "Active";
                          }
                        } else {
                          orderStatusText = Number(order.StatusID) === 3 ? "Cancelled" : "Completed";
                        }

                        return (
                          <TableRow
                            key={order.ThreadID || index}
                            className={`${connDetailsClasses.hoverBg} border-gray-100`}
                            onClick={() => {
                              setSelectedThread(order);
                              if (isOpenOrder) {
                                setDialogStates(prev => ({ ...prev, thrDetails: true }));
                              } else {
                                setDialogStates(prev => ({ ...prev, threadDetails: true, isHistoryview: true }));
                              }
                            }}
                          >
                            {hasAnyOrderReference && <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              <div className={connDetailsClasses.truncate} title={order.Reference && order.Reference !== "0" && order.Reference !== 0 ? order.Reference : "--"}>
                                {order.Reference && order.Reference !== "0" && order.Reference !== 0 ? order.Reference : "--"}
                              </div>
                            </TableCell>}
                            <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              <div className={connDetailsClasses.truncate} title={order.MainProduct || order.Product || "--"}>
                                {order.MainProduct || order.Product || "--"}
                              </div>
                            </TableCell>
                            <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              <div className={connDetailsClasses.truncate}>
                                {order.SinceDT ? getDateWithoutTime(order.SinceDT) : "--"}
                              </div>
                            </TableCell>
                            <TableCell className={`hidden md:table-cell ${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-right tabular-nums`}>
                              <div className={connDetailsClasses.truncate}>
                                {order.Amount || order.BizProspect || order.bizProspect
                                  ? `${(order.Amount || order.BizProspect || order.bizProspect).toLocaleString("en-US")}`
                                  : "--"}
                              </div>
                            </TableCell>
                            <TableCell className={`hidden xl:table-cell ${connDetailsClasses.tableCell} text-left`}>
                              {orderStatusText && orderStatusText !== "--" ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={connDetailsClasses.truncate}>
                                      <span>{orderStatusText}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p>{orderStatusText}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <div className={connDetailsClasses.truncate}>--</div>
                              )}
                            </TableCell>
                            <TableCell className={`hidden lg:table-cell ${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              {(order.FdbkScore > 0 || (order.Notes && order.Notes !== "--")) ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={connDetailsClasses.truncate}>
                                      {order.FdbkScore > 0 && (
                                        <span className="inline-flex items-center gap-1 mr-1.5">
                                          {sentimentEmoji(order.FdbkScore)}
                                          {order.FailureReason && <span className="font-semibold text-gray-700">({order.FailureReason})</span>}
                                        </span>
                                      )}
                                      <span>{order.FdbkNotes || order.Notes || "--"}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    {order.FdbkScore > 0 && (
                                      <p className="font-medium mb-0.5">
                                        Sentiment: {getFdbkScoreText(order.FdbkScore)} {sentimentEmoji(order.FdbkScore)}
                                        {order.FailureReason && ` - ${order.FailureReason}`}
                                      </p>
                                    )}
                                    <p>{order.FdbkNotes || order.Notes}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <div className={connDetailsClasses.truncate}>--</div>
                              )}
                            </TableCell>
                            <TableCell className={`${connDetailsClasses.tableCell} text-right`}>
                              {isOpenOrder && isOwnAgencyConnection && (order.ConnID == connection.ConnID) && hasAgencyAccess ? (
                                <div className="flex items-center justify-end gap-1 xl:gap-2">
                                  {/* Complete Action */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={connDetailsClasses.trueActionButton}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const threadName = getThreadNameFromTypeID(order?.TypeID || 2, threadTypesData, "SnSng");
                                          const threadNameLower = threadName?.toLowerCase();

                                          if (isPendingTasks(order?.ThreadID,
                                            `Cannot complete ${threadName}`,
                                            `Please complete tasks before completing this ${threadNameLower}.`)
                                          ) {
                                            return;
                                          }
                                          if (!threadActionLoading[order.ThreadID]) {
                                            handleItemAction('COMPLETE', order, 'thread', e);
                                          }
                                        }}
                                        disabled={threadActionLoading[order.ThreadID]}
                                      >
                                        {threadActionLoading[order.ThreadID] === 'completing' ? (
                                          <div className={`h-3 w-3 ${connDetailsClasses.loadingSpinner} border-sahi-green border-t-transparent`} />
                                        ) : (
                                          <Check className="h-3 w-3 text-sahi-green" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Mark as complete</p>
                                    </TooltipContent>
                                  </Tooltip>

                                  {/* Cancel Action */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={connDetailsClasses.falseActionButton}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isPendingTasks(
                                            order?.ThreadID,
                                            'Cannot cancel sale.',
                                            'Please complete tasks before cancelling this sale.'
                                          )) {
                                            return;
                                          }
                                          if (!threadActionLoading[order.ThreadID]) {
                                            handleItemAction('CANCEL', order, 'thread', e);
                                          }
                                        }}
                                        disabled={threadActionLoading[order.ThreadID]}
                                      >
                                        {threadActionLoading[order.ThreadID] === 'cancelling' ? (
                                          <div className={`h-3 w-3 ${connDetailsClasses.loadingSpinner} border-sahi-red border-t-transparent`} />
                                        ) : (
                                          <X className="h-3 w-3 text-sahi-red" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Cancel sale</p>
                                    </TooltipContent>
                                  </Tooltip>

                                  {/* Edit Action */}
                                  {canShowEditButton && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className={connDetailsClasses.editActionButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleItemAction('EDIT', order, 'thread', e);
                                            setLocalStates((prev) => ({
                                              ...prev,
                                              selectedThreadType: 2,
                                            }));
                                          }}
                                        >
                                          <Pencil className="h-3 w-3 text-sahi-blue" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Edit sale</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-1 xl:gap-2">
                                  {(order.ThrFile || order.thrFile || order.thrfile) && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className={connDetailsClasses.editActionButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const filePath = order.ThrFile || order.thrFile || order.thrfile;
                                            setPreviewFilePath(filePath);
                                          }}
                                        >
                                          <Paperclip className="h-3.5 w-3.5 text-sahi-blue" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>View Attached Document</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                  {Number(order.StatusID) === 2 && isOwnAgencyConnection && (connection?.Mobile || order?.Mobile) && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className={connDetailsClasses.trueActionButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleFdbkRequest(order);
                                          }}
                                        >
                                          <MessageSquareText className="h-3.5 w-3.5 text-sahi-teal" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Send feedback request on WhatsApp</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };


  const TasksTable = () => {
    const tasks = getTasksData();
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const scrollableRef = useRef(null);
    const [expandedTaskThreads, setExpandedTaskThreads] = useState(new Set());

    // This whole component remounts on every ConnectionDetails re-render (see tasksScrollTopRef
    // comment above), so restore the last known scroll position right after mount instead of
    // trying to preserve the (now-destroyed) old DOM node.
    useLayoutEffect(() => {
      if (scrollableRef.current) {
        scrollableRef.current.scrollTop = tasksScrollTopRef.current;
      }
    }, []);

    useEffect(() => {
      const checkScrollbar = () => {
        if (scrollableRef.current) {
          const hasVerticalScrollbar = scrollableRef.current.scrollHeight > scrollableRef.current.clientHeight;
          setHasScrollbar(hasVerticalScrollbar);
        }
      };

      checkScrollbar();
      const timeoutId = setTimeout(checkScrollbar, 100);
      return () => clearTimeout(timeoutId);
    }, [tasks]);

    const toggleTaskThreads = (taskID) => {
      setExpandedTaskThreads(prev => {
        const newSet = new Set(prev);
        if (newSet.has(taskID)) {
          newSet.delete(taskID);
        } else {
          newSet.add(taskID);
        }
        return newSet;
      });
    };

    return (
      <div className="space-y-3">
        <div className={connDetailsClasses.flexBetween}>
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>
            {tasks.length > 0 ? `Tasks (${tasks.length})` : 'Tasks'}
          </h3>
          <div className="flex items-center gap-2">
            <Button
              className="flex items-center"
              onClick={() => setDialogStates((prev) => ({ ...prev, taskEntry: true }))}
            >
              <Plus className="h-4 w-4" />
              Enter Task
            </Button>
          </div>
        </div>

        {tasks.length > 0 ? (
          <Card className="bg-white overflow-hidden lg:!pt-0">
            <CardContent className="p-0 relative">
              {/* Container for both header and body to maintain alignment */}
              <div className="table-container">
                {/* Fixed header */}
                <div
                  className="bg-white border-b border-gray-200 overflow-hidden"
                  style={{
                    marginRight: hasScrollbar ? '8px' : '0px',
                    transition: 'margin-right 0.1s ease'
                  }}
                >
                  <Table className="table-fixed-layout">
                    <colgroup className="tasks-table-cols">
                      <col style={{ width: '100px' }} />
                      <col style={{ width: 'auto' }} />
                      <col style={{ width: '120px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '140px' }} />
                    </colgroup>
                    <TableHeader className="!border-0">
                      <TableRow className="bg-white !border-0">
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Type</TableHead>
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Task</TableHead>
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Finish by</TableHead>
                        <TableHead className={`table-header-text ${connDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Executive</TableHead>
                        <TableHead className="table-header-text whitespace-nowrap px-3 py-2 text-left"></TableHead>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </div>
              </div>

              {/* Scrollable body */}
              <div
                ref={scrollableRef}
                className="table-scrollable-body  !max-h-[150px] !overflow-y-auto"
                onScroll={(e) => { tasksScrollTopRef.current = e.target.scrollTop; }}
              >                <Table className="table-fixed-layout">
                  <colgroup className="tasks-table-cols">
                    <col style={{ width: '100px' }} />
                    <col style={{ width: 'auto' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '140px' }} />
                  </colgroup>
                  <TableBody>
                    {tasks?.map((task, index) => {
                      const taskID = task.TskID;
                      const isLoading = taskActionLoading[taskID];
                      const hasThreads = task.TIDs && typeof task.TIDs === 'string' && task.TIDs.trim();
                      const isThreadsExpanded = expandedTaskThreads.has(taskID);
                      const isCompleted = task.Sts === 2;
                      const isCancelled = task.Sts === 0;
                      const myUserIDs = getMyAgenciesMappedIDs('UserID');
                      const taskCreatorID = Number(task.CreatedUID || task.UID || 0);
                      const isCreator = myUserIDs.includes(taskCreatorID);
                      const isAssignee = myUserIDs.includes(Number(task.UID)) || Number(task.UID) === Number(currentUserID);
                      const canComplete = !isCompleted && !isCancelled && (isCreator || (isAssignee && task.CanComplete == true));
                      // Only the task's creator (checked across all of their agency profiles via
                      // getMyAgenciesMappedIDs) can cancel - same rule as edit (canEditTask).
                      const canCancel = !isCancelled && isCreator;

                      return (
                        <React.Fragment key={task.TskID || index}>
                          <TableRow
                            className={`${connDetailsClasses.hoverBg} border-b border-gray-100 cursor-pointer`}
                            onClick={(e) => handleItemAction('SELECT', task, 'task', e)}
                          >
                            <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                              <div className={connDetailsClasses.truncate} title={task.Typ || task.type || "General"}>
                                {task.Typ || task.type || "-"}
                              </div>
                            </TableCell>

                            <TableCell className={`${connDetailsClasses.tableCell} text-left`}>
                              <div className="flex flex-col gap-1">
                                <div
                                  className={`text-sm font-medium ${connDetailsClasses.textColor} ${connDetailsClasses.truncate} flex items-center gap-1.5`}
                                  title={task.Ttl || task.title || "Untitled Task"}
                                  style={{ maxWidth: '100%' }}
                                >
                                  {task.Pri && (() => {
                                    const priority = getPriorityDetails(task.Pri);
                                    if (!priority.showBadge) return null;
                                    const IconComponent = priority.icon;
                                    return (
                                      <span style={{ color: priority.color }} title={`${priority.label} Priority`}>
                                        <IconComponent className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={2.5} />
                                      </span>
                                    );
                                  })()}
                                  <span>{task.Ttl || task.title || "Untitled Task"}</span>
                                  {(() => {
                                    if (!task.TIDs) return null;

                                    const filteredCount = task.TIDs.split(',').filter((threadId) => {
                                      const thread = getThreadsData().find(t => t.ThreadID === parseInt(threadId.trim()));
                                      return thread && thread.StatusID === 0;
                                    }).length;

                                    return filteredCount > 0 ? (
                                      <span className="ml-2 text-xs text-gray-400">
                                        ({filteredCount})
                                      </span>
                                    ) : null;
                                  })()}
                                </div>

                                {isThreadsExpanded && hasThreads && task.TIDs && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                                    {task.TIDs.split(',').filter((threadId) => {
                                      const thread = getThreadsData().find(t => t.ThreadID === parseInt(threadId.trim()));
                                      return thread && thread.StatusID === 0;
                                    }).map((threadId, threadIndex) => {
                                      const thread = getThreadsData().find(t => t.ThreadID === parseInt(threadId.trim()));
                                      let displayText;
                                      let threadIcon = null;
                                      let threadTypeMeta = null;

                                      if (!thread) {
                                        displayText = `Thread #${threadId.trim()}`;
                                      } else {
                                        threadTypeMeta = getThreadTypeMeta(thread.TypeID || thread.typeID, threadTypesData);
                                        if (threadTypeMeta) {
                                          threadIcon = threadTypeMeta.Icon;
                                        }
                                        const threadType = threadTypeMeta ? threadTypeMeta.nameSng : (thread.TypeName || thread.Type || "General");
                                        const product = thread.MainProduct || thread.product || thread.Product || "No Product";
                                        displayText = `${threadType} - ${product}`;
                                      }

                                      const hasDateInfo = thread && (thread.targetDT || thread.sinceDT || thread.DueDT);
                                      const sinceDT = thread && thread.SinceDT ? getRelativeDateText(new Date(thread.SinceDT), false, false) : '';
                                      const targetDT = thread && thread.DueDT ? getRelativeDateText(new Date(thread.DueDT), false, false) : '';

                                      const BadgeContent = (
                                        <div className="w-full">
                                          <Button variant="ghost" size="sm" className="w-full justify-start p-0 h-auto">
                                            <Badge className={`w-full justify-start h-7 ${connDetailsClasses.badgeBase} px-2`}>
                                              {threadIcon && <ThreadSahiIcon tID={thread.TypeID} className='mr-2 flex-shrink-0' />}
                                              <span className={`${connDetailsClasses.truncate} text-xs`}>{displayText}</span>
                                            </Badge>
                                          </Button>
                                        </div>
                                      );

                                      return hasDateInfo ? (
                                        <HoverCard key={`${taskID}-thread-${threadId.trim()}`}>
                                          <HoverCardTrigger asChild>{BadgeContent}</HoverCardTrigger>
                                          <HoverCardContent className="bg-white sahi-radius shadow-2xl p-3 w-40 text-left" side="top">
                                            <h4 className="text-sm font-bold mb-2">
                                              {threadTypeMeta ? threadTypeMeta.nameSng : (thread?.ThreadName || thread?.Type || "General")}
                                            </h4>
                                            <div>
                                              {targetDT && (
                                                <div>
                                                  <span className="text-muted-foreground">Due on: </span>
                                                  <span>{targetDT}</span>
                                                </div>
                                              )}
                                              {sinceDT && (
                                                <div>
                                                  <span className="text-muted-foreground">Since: </span>
                                                  <span>{sinceDT}</span>
                                                </div>
                                              )}
                                            </div>
                                          </HoverCardContent>
                                        </HoverCard>
                                      ) : (
                                        <div key={`${taskID}-thread-${threadId.trim()}`}>{BadgeContent}</div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </TableCell>

                            <TableCell className={connDetailsClasses.tableCell}>
                              <div className="text-left">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={`text-sm font-medium ${getColorClass(getTaskColor(task.DT))} whitespace-nowrap`}>
                                      {getRelativeDateText(task.DT, false, false, false)}
                                    </div>
                                  </TooltipTrigger>
                                  {task.DT && (
                                    <TooltipContent className="time-tooltip">
                                      <p>{format(new Date(task.DT), "dd-MMM")}</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </div>
                            </TableCell>

                            <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor}`}>
                              <div className={`text-left ${connDetailsClasses.truncate}`}>
                                {getExecutiveName(task.UID)}
                              </div>
                            </TableCell>

                            <TableCell className={connDetailsClasses.tableCell}>
                              {!task?.SCIDs?.split(",").map(Number).includes(connection?.ConnID) && <div className="flex items-start gap-1.5 xl:gap-3">

                                {/* Remind Action */}
                                {task.POCUserID && task.POCUserID !== currentUserID && task.CreatedUID === currentUserID ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-blue-light`}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleRemindTask(task);
                                        }}
                                      >
                                        {isLoading === 'reminding' ? (
                                          <div className={`h-3 w-3 border-sahi-blue border-t-transparent`} />
                                        ) : (
                                          <BsBellFill className="h-3 w-3 text-sahi-blue" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Remind {getExecutiveName(task.UID)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-text-color h-6 w-6 flex items-center justify-center leading-none"></span>
                                )}

                                {/* Edit Action */}
                                {canEditTask(task) && <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={connDetailsClasses.editActionButton}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleItemAction('EDIT', task, 'task', e)
                                      }}
                                    >
                                      <Pencil className="h-3 w-3 text-sahi-blue" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit Task</p>
                                  </TooltipContent>
                                </Tooltip>}

                                {/* Complete Action */}
                                {canComplete ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={connDetailsClasses.trueActionButton}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (!isLoading) {
                                            handleItemAction('COMPLETE', task, 'task', e);
                                          }
                                        }}
                                        disabled={isLoading}
                                      >
                                        {isLoading === 'completing' ? (
                                          <div className={`h-3 w-3 ${connDetailsClasses.loadingSpinner} border-sahi-green border-t-transparent`} />
                                        ) : (
                                          <Check className="h-3 w-3 text-sahi-green" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Mark as complete</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-text-color h-6 w-6 flex items-center justify-center leading-none"></span>
                                )}

                                {/* Cancel Action */}
                                {canCancel ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={connDetailsClasses.falseActionButton}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (!isLoading) {
                                            handleItemAction('CANCEL', task, 'task', e);
                                          }
                                        }}
                                        disabled={isLoading}
                                      >
                                        {isLoading === 'cancelling' ? (
                                          <div className={`h-3 w-3 ${connDetailsClasses.loadingSpinner} border-sahi-red border-t-transparent`} />
                                        ) : (
                                          <X className="h-3 w-3 text-sahi-red" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Cancel task</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (!isCancelled && !isCompleted && !isCreator && isAssignee) ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={connDetailsClasses.falseActionButton}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          if (!isLoading) {
                                            handleItemAction('CANCEL', task, 'task', e);
                                          }
                                        }}
                                        disabled={isLoading}
                                      >
                                        {isLoading === 'cancelling' ? (
                                          <div className={`h-3 w-3 ${connDetailsClasses.loadingSpinner} border-sahi-red border-t-transparent`} />
                                        ) : (
                                          <X className="h-3 w-3 text-sahi-red" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Reject task</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-text-color h-6 w-6 flex items-center justify-center leading-none"></span>
                                )}
                              </div>}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <NoDataCard
            Icon={ListTodo}
            title="No tasks found."
            desc="Click to add a task."
            onClick={() => setDialogStates((prev) => ({ ...prev, taskEntry: true }))}
          />
        )}
      </div>
    );
  };

  // Transaction Table 
  const TransactionTable = () => {
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const scrollableRef = useRef(null);

    useEffect(() => {
      const checkScrollbar = () => {
        if (scrollableRef.current) {
          const hasVerticalScrollbar =
            scrollableRef.current.scrollHeight > scrollableRef.current.clientHeight;
          setHasScrollbar(hasVerticalScrollbar);
        }
      };

      checkScrollbar();
      const timeoutId = setTimeout(checkScrollbar, 100);
      return () => clearTimeout(timeoutId);
    }, [transactions]);

    if (!showModule.transactions) return null;

    return (
      <div className="space-y-3">
        <div className={connDetailsClasses.flexBetween}>
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>
            {transactions.length > 0
              ? `Transactions (${transactions.length})`
              : "Transactions"}
          </h3>
          <div className="flex items-center gap-2">
            {isOwnAgencyConnection && <TransactionDropdown
              onTransactionTypeSelect={(typeId) => {
                setSelectedTransactionType(typeId);
                setSelectedTransaction(null);
                setTransactionPrefilledData({});
                setDialogStates((prev) => ({ ...prev, transactionEntry: true }));
              }}
              connectionType={connection?.ConnType}
              externalOpen={transactionDropdownOpen}
              onExternalOpenChange={setTransactionDropdownOpen}
            />}
          </div>
        </div>

        {transactions && transactions.length > 0 ? (
          <Card className="bg-white overflow-hidden lg:!pt-0">
            <CardContent className="p-0 relative">
              {/* Header */}
              <div
                className="bg-white border-b border-gray-200 overflow-hidden"
                style={{
                  marginRight: hasScrollbar ? "8px" : "0px",
                  transition: "margin-right 0.1s ease",
                }}
              >
                <Table className="table-fixed w-full">
                  <colgroup>
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <TableHeader className="!border-0">
                    <TableRow className="bg-white !border-0">
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Type
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Ref No.
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Date
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Amount
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Contact
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-right`}>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                </Table>
              </div>

              {/* Scrollable body */}
              <div
                ref={scrollableRef}
                className="table-scrollable-body !max-h-[150px] overflow-y-auto overflow-x-hidden"
              >
                <Table className="table-fixed w-full">
                  <colgroup>
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <TableBody>
                    {transactions.map((transaction, index) => {
                      const transactionID = transaction.XnID;
                      const transactionType = getTransactionTypeById(transaction.XnTypeID);
                      const contactName = transaction.IsSeller ? transaction.BuyerUserName : transaction.SellerUserName;

                      return (
                        <TableRow
                          key={transactionID}
                          className={`${connDetailsClasses.hoverBg} border-b border-gray-100`}
                        >
                          {/* Transaction Type */}
                          <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                            <div className="flex items-center">
                              <div className={getTransactionIconColorClass(transactionType)}>
                                {getTransactionIcon(transactionType)}
                              </div>
                              <div className={`${connDetailsClasses.truncate} ml-2 text-sm`}>
                                {transactionType?.LblF}
                              </div>
                            </div>
                          </TableCell>
                          {/* Transaction Number */}
                          <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                            <div className={connDetailsClasses.truncate} title={transaction.XnNo}>
                              {transaction.XnNo}
                            </div>
                          </TableCell>

                          {/* Transaction Date */}
                          <TableCell className={`${connDetailsClasses.tableCell} text-left`}>
                            <div className="text-sm">
                              {transaction.XnDate ? getDateWithoutTime(transaction.XnDate) : "-"}
                            </div>
                          </TableCell>

                          {/* Amount */}
                          <TableCell className={`${connDetailsClasses.tableCell} text-right`}>
                            <div className="text-sm font-medium text-right">
                              <FormatAmount
                                amount={transaction.AmtGrandTotal}
                                currency={getCurrencySymbol(transaction.CurrencyID)}
                              />
                            </div>
                          </TableCell>

                          {/* Contact Person */}
                          <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                            <div className={connDetailsClasses.truncate} title={contactName}>
                              {contactName}
                            </div>
                          </TableCell>

                          {/* Actions */}
                          <TableCell className={`${connDetailsClasses.tableCell} text-right`}>
                            <div className="flex items-center justify-end gap-[2px] xl:gap-2.5">
                              {transaction.XnFile && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={connDetailsClasses.editActionButton}
                                      onClick={(e) => handleItemAction("DOWNLOAD", transaction, "transaction", e)}
                                    >
                                      <Paperclip className="h-3 w-3 text-sahi-blue" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Download PDF</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              <ContactActions
                                connectionData={{
                                  connID: connection?.ConnID || "",
                                  contactName: connection.ContactName || "",
                                  bizName: connection?.BizName || "",
                                  mobile: transaction.IsSeller ? transaction.BuyerMobile : transaction.SellerMobile,
                                  email: transaction.IsSeller ? transaction.BuyerEmail : transaction.SellerEmail,
                                  cc: transaction.IsSeller ? transaction.BuyerCC : transaction.SellerCC,
                                }}
                              />

                              {canEditXn && <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={connDetailsClasses.editActionButton}
                                    onClick={(e) => handleItemAction("EDIT", transaction, "transaction", e)}
                                  >
                                    <Pencil className="h-3 w-3 text-sahi-blue" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Edit Transaction</p>
                                </TooltipContent>
                              </Tooltip>}

                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <NoDataCard
            staticIcon={currencySymbol}
            title={`No transactions found.`}
            desc={`Click to add a transaction.`}
            onClick={() => setTransactionDropdownOpen(true)}
          />
        )}
      </div>
    );
  };

  const ContractsTable = () => {
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const scrollableRef = useRef(null);

    useEffect(() => {
      const checkScrollbar = () => {
        if (scrollableRef.current) {
          const hasVerticalScrollbar =
            scrollableRef.current.scrollHeight > scrollableRef.current.clientHeight;
          setHasScrollbar(hasVerticalScrollbar);
        }
      };

      checkScrollbar();
      const timeoutId = setTimeout(checkScrollbar, 100);
      return () => clearTimeout(timeoutId);
    }, [contracts]);

    // Sort contracts by expiry date in ascending order
    const sortedContracts = useMemo(() => {
      if (!contracts || contracts.length === 0) return [];

      return [...contracts].sort((a, b) => {
        const dateA = a.ExpiryDT ? new Date(a.ExpiryDT) : new Date('9999-12-31'); // Put null dates at the end
        const dateB = b.ExpiryDT ? new Date(b.ExpiryDT) : new Date('9999-12-31');

        return dateA - dateB; // Ascending order
      });
    }, [contracts]);

    const handleClick = () => {
      if (!canAddCtrc) return; // do nothing if not allowed

      if (connection?.ConnType === "C") {
        setSelectedContractType("sold");
        setSelectedContract(null);
        setContractPrefilledData({});
        setDialogStates((prev) => ({ ...prev, contractEntry: true }));
      } else if (connection?.ConnType === "S") {
        setSelectedContractType("purchase");
        setSelectedContract(null);
        setContractPrefilledData({});
        setDialogStates((prev) => ({ ...prev, contractEntry: true }));
      } else {
        setContractDropdownOpen(true);
      }
    };

    if (!showModule.contracts) return null;

    return (
      <div className="space-y-3">
        <div className={connDetailsClasses.flexBetween}>
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>
            {contracts.length > 0
              ? `Contracts (${contracts.length})`
              : "Contracts"}
          </h3>
          <div className="flex items-center gap-2">
            {isOwnAgencyConnection && <ContractDropdown
              onContractTypeSelect={(contractType) => {
                setSelectedContractType(contractType);
                setSelectedContract(null);
                setContractPrefilledData({});
                setDialogStates((prev) => ({ ...prev, contractEntry: true }));
              }}
              connectionType={connection?.ConnType}
              externalOpen={contractDropdownOpen}
              onExternalOpenChange={setContractDropdownOpen}
            />}
          </div>
        </div>

        {contracts && contracts.length > 0 ? (
          <Card className="bg-white overflow-hidden lg:!pt-0">
            <CardContent className="p-0 relative">
              {/* Header */}
              <div
                className="bg-white border-b border-gray-200 overflow-hidden"
                style={{
                  marginRight: hasScrollbar ? "8px" : "0px",
                  transition: "margin-right 0.1s ease",
                }}
              >
                <Table className="table-fixed w-full">
                  <colgroup>
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "26%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <TableHeader className="!border-0">
                    <TableRow className="bg-white !border-0">
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Ref No.
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Type
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Offering
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Charges ({currencySymbol})
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Expiry
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-left`}>
                        Renewal ({currencySymbol})
                      </TableHead>
                      <TableHead className={`${connDetailsClasses.dialogHeader} text-right`}>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                </Table>
              </div>

              {/* Scrollable body */}
              <div
                ref={scrollableRef}
                className="table-scrollable-body !max-h-[150px] overflow-y-auto overflow-x-hidden"
              >
                <Table className="table-fixed w-full">
                  <colgroup>
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "26%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <TableBody>
                    {sortedContracts
                      .filter(contract => contract.StatusID === 0)
                      .map((contract, index) => {
                        const contractID = contract.ContractID;

                        return (
                          <React.Fragment key={contractID}>
                            <TableRow
                              className={`${connDetailsClasses.hoverBg} border-b border-gray-100 cursor-pointer`}
                              onClick={() => handleItemAction('SELECT', contract, 'contract')}
                            >
                              {/* Contract Number */}
                              <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                                <div className={connDetailsClasses.truncate} title={contract.ContractNo}>
                                  {contract.ContractNo}
                                </div>
                              </TableCell>

                              {/* Contract Type */}
                              <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                                <div className={connDetailsClasses.truncate} title={contract.ContractType}>
                                  {contract.ContractType}
                                </div>
                              </TableCell>

                              {/* Product */}
                              <TableCell className={`${connDetailsClasses.tableCell} ${connDetailsClasses.textColor} text-left`}>
                                <div className={connDetailsClasses.truncate} title={contract.Product}>
                                  {contract.Product || "--"}
                                </div>
                              </TableCell>

                              {/* Charges - Fixed to show ContractCharges instead of RenewalCharges */}
                              <TableCell className={`${connDetailsClasses.tableCell} text-right`}>
                                <div className="text-sm font-medium text-right">
                                  {contract.ContractCharges ? (
                                    <span className="tabular-nums">
                                      {contract.ContractCharges.toLocaleString("en-US")}
                                    </span>
                                  ) : "--"}
                                </div>
                              </TableCell>

                              {/* Expiry Date */}
                              <TableCell className={`${connDetailsClasses.tableCell} text-left`}>
                                <div className={`text-sm ${getColorClass(getTaskColor(contract.ExpiryDT))} whitespace-nowrap`}>
                                  {contract.ExpiryDT ? getDateWithoutTime(contract.ExpiryDT) : "--"}
                                </div>
                              </TableCell>

                              {/* Renewal Charges - RIGHT ALIGNED */}
                              <TableCell className={`${connDetailsClasses.tableCell} text-right`}>
                                <div className="text-sm font-medium text-right">
                                  {contract.RenewalCharges ? (
                                    <span className="tabular-nums">
                                      {contract.RenewalCharges.toLocaleString("en-US")}
                                    </span>
                                  ) : "--"}
                                </div>
                              </TableCell>

                              {/* Actions */}
                              <TableCell className={`${connDetailsClasses.tableCell} text-right`}>
                                <div className="flex items-center justify-end gap-[2px] xl:gap-2.5">
                                  {contract.Attachment && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className={connDetailsClasses.editActionButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (contract.Attachment) {
                                              const fileUrl = `/Uploads/${contract.Attachment}`;
                                              window.open(fileUrl, "_blank");
                                            }
                                          }}
                                        >
                                          <Paperclip className="h-3 w-3 text-sahi-blue" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Download Contract</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                  {canEditContracts &&
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className={connDetailsClasses.editActionButton}
                                          onClick={(e) => handleItemAction("EDIT", contract, "contract", e)}
                                        >
                                          <Pencil className="h-3 w-3 text-sahi-blue" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Edit Contract</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  }
                                </div>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <NoDataCard
            Icon={FileText}
            title="No Contracts found."
            desc="Click to add a contract."
            onClick={handleClick}
          />
        )}
      </div>
    );
  };

  // Last interaction card component
  const LastInteractionCard = () => {
    const sortedRecords = records
      .filter(record => record && record.DT)
      .sort((a, b) => new Date(b.DT) - new Date(a.DT))
    const lastRecord = useMemo(() => {
      if (sortedRecords.length === 0 || interactionRecordTypeNames.size === 0) return null;
      return sortedRecords.find(record => interactionRecordTypeNames.has(record.Typ)) || null;
    }, [sortedRecords, interactionRecordTypeNames]);

    const callCount = records.filter(record => record && record.Typ === 'Call' && record.Txt === 'Dialed Call.').length;
    const messageCount = records.filter(record => record && record.Typ === 'Message' && record.Txt === 'Sent Message.').length;

    return (
      <div className="space-y-3">
        <div className={connDetailsClasses.flexBetween}>
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>Last Interaction</h3>
          <div className="flex items-center gap-2">
            {hasAgencyAccess && <Button onClick={() => setDialogStates((prev) => ({ ...prev, recordEntry: true }))}>
              <Plus className="h-4 w-4" />
              {lastRecord?.Txt ? "Add" : "Enter"} Interaction
            </Button>}
            <Button
              className="px-3"
              variant="secondary"
              onClick={() => setDialogStates((prev) => ({ ...prev, recordHistory: true }))}
            >
              <History className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* {(callCount > 0 || messageCount > 0) && (
          <div className="flex flex-wrap gap-2 text-xs">
            {callCount > 0 && (
              <div 
                className="inline-block bg-white px-3 py-2 sahi-radius text-foreground cursor-pointer"
                onClick={() => setDialogStates((prev) => ({ ...prev, recordHistory: true, recordTypeFilter: 'Call' }))}
              >
                <span>Calls: </span>
                <span className="font-semibold">{callCount}</span>
              </div>
            )}
            {messageCount > 0 && (
              <div 
                className="inline-block bg-white px-3 py-2 sahi-radius text-foreground cursor-pointer"
                onClick={() => setDialogStates((prev) => ({ ...prev, recordHistory: true, recordTypeFilter: 'Message' }))}
              >
                <span>Messages: </span>
                <span className="font-semibold">{messageCount}</span>
              </div>
            )}
          </div>
        )} */}
        {localStates.loadingRecords ? (
          <RecordShimmer />
        ) : lastRecord ? (
          <div className="space-y-3">
            <div className={`${connDetailsClasses.cardInteractionBase} p-3 h-auto`}>
              <p className={`text-sm ${connDetailsClasses.textColor} mb-3`}>
                {/* {formatRecordText(lastRecord.Txt)} */}
                {lastRecord.Txt}
              </p>
              <div className={`${connDetailsClasses.flexBetween} text-xs ${connDetailsClasses.textSubColor}`}>
                <span>@ {getRelativeDateText(lastRecord.DT, false, false, false)}</span>
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {getExecutiveName(lastRecord.UID)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <NoDataCard
            Icon={History}
            title="No interactions recorded."
            desc="Click to add an interaction."
            onClick={() => hasAgencyAccess && setDialogStates((prev) => ({ ...prev, recordEntry: true }))}
          />
        )}
      </div>
    )
  }

  const isTodayOrYesterday = (dateString) => {
    if (!dateString) return false;
    const recordDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const recordDay = recordDate.toDateString();
    const todayDay = today.toDateString();
    const yesterdayDay = yesterday.toDateString();

    return recordDay === todayDay || recordDay === yesterdayDay;
  };

  const PendingDetailsCard = () => {
    // Filter records to show only those assigned to the user (matching RIDs)
    const userAssignedRecords = records.filter(record =>
      record && record.RID && userPendingRIDs.includes(String(record.RID))
    );

    const callCount = userAssignedRecords.filter(record =>
      record.Typ === 'Call' && record.Txt === 'Dialed Call.' && isTodayOrYesterday(record.DT)
    ).length;

    const messageCount = userAssignedRecords.filter(record =>
      record.Typ === 'Message' && record.Txt === 'Sent Message.' && isTodayOrYesterday(record.DT)
    ).length;

    // Only show card if at least one count is greater than 0
    if (callCount === 0 && messageCount === 0) {
      return null;
    }

    return (
      <div className="space-y-3">
        <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor} mt-3`}>
          Interaction Details Pending ({messageCount + callCount})
        </h3>
        <div className={`${connDetailsClasses.cardBase} p-3`}>
          <div className="flex gap-2">
            {callCount > 0 && (
              <Button
                size="sm"
                className="h-auto py-1 px-3 text-xs bg-gray-100 text-foreground flex items-center justify-between"
                onClick={() => setDialogStates((prev) => ({ ...prev, recordHistory: true, recordTypeFilter: 'Call' }))}
              >
                <span className="text-sm">Calls <span className="text-gray-900">({callCount})</span></span>
                <Pencil className="h-3 w-3 text-sahi-blue flex-shrink-0 ml-2" />
              </Button>
            )}
            {messageCount > 0 && (
              <Button
                size="sm"
                className="h-auto py-1 px-3 text-xs bg-gray-100 text-foreground flex items-center justify-between"
                onClick={() => setDialogStates((prev) => ({ ...prev, recordHistory: true, recordTypeFilter: 'Message' }))}
              >
                <span className="text-sm">Messages <span className="text-gray-900">({messageCount})</span></span>
                <Pencil className="h-3 w-3 text-sahi-blue flex-shrink-0 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Function which will handle sentiment request
  const handleFdbkRequest = async (targetItem = null) => {
    // If targetItem is a thread/order, trigger thread feedback request (FDBKREQ on module 1)
    const isThreadItem = Boolean(targetItem && (targetItem.ThreadID || targetItem.threadID || targetItem.TypeID || targetItem.typeID));
    if (isThreadItem) {
      dispatch(setGlobalLoading(true));
      try {
        const threadData = {
          ...targetItem,
          threadID: targetItem.ThreadID || targetItem.threadID || targetItem.actionID,
          typeID: targetItem.TypeID || targetItem.typeID || 2,
          pocUserID: targetItem.POCUserID || targetItem.pocUserID || 0,
          shareByWhatsApp: true,
          shareByEmail: false,
          takeFeedback: true,
          connID: targetItem.ConnID || targetItem.connID || connection?.ConnID,
          reference: targetItem.Reference || targetItem.reference,
          product: targetItem.MainProduct || targetItem.mainProduct || targetItem.Product || targetItem.product,
          connectionData: {
            bizName: targetItem.BizName || targetItem.bizName || targetItem.connectionData?.bizName || targetItem.connectionData?.BizName || connection?.BizName,
            contactName: targetItem.ContactName || targetItem.contactName || targetItem.ContactPerson || targetItem.connectionData?.contactName || targetItem.connectionData?.ContactName || connection?.ContactName,
            email: targetItem.Email || targetItem.email || targetItem.connectionData?.email || targetItem.connectionData?.Email || connection?.Email,
            mobile: targetItem.Mobile || targetItem.mobile || targetItem.connectionData?.mobile || targetItem.connectionData?.Mobile || connection?.Mobile,
            cc: targetItem.CC || targetItem.cc || targetItem.connectionData?.cc || targetItem.connectionData?.CC || connection?.CC
          }
        };

        const result = await performThreadActionAPI(threadData, 'FDBKREQ');
        if (result.error === false) {
          toast.success("Feedback request sent on WhatsApp.");
        } else {
          toast.error(result.message || "Failed to send feedback request.");
        }
      } catch (err) {
        console.error('Error while sending feedback request', err);
        toast.error("Failed to send feedback request.");
      } finally {
        dispatch(setGlobalLoading(false));
      }
      return;
    }

    const sendByTool = {
      sendByEmail: false,
      sendByWsp: true
    }

    const connMobile = targetItem?.Mobile || targetItem?.connMobile || targetItem?.connectionData?.Mobile || connection?.Mobile || "";
    const connCC = targetItem?.CC || targetItem?.connCC || targetItem?.connectionData?.CC || connection?.CC || "91";
    const connIDVal = targetItem?.ConnID || targetItem?.connID || connection?.ConnID;
    const bizNameVal = targetItem?.BizName || targetItem?.bizName || targetItem?.connectionData?.BizName || connection?.BizName || "";
    const contactNameVal = targetItem?.ContactName || targetItem?.contactName || targetItem?.ContactPerson || targetItem?.connectionData?.ContactName || connection?.ContactName || "";
    const emailVal = targetItem?.Email || targetItem?.email || targetItem?.connectionData?.Email || connection?.Email || "";

    const apiData = {
      connID: connIDVal,
      connMobile: connMobile,
      connCC: connCC,
      bizName: bizNameVal,
      contactPerson: contactNameVal,
      email: emailVal,
      userMobile: currentUser.Mobile,
      username: currentUser.Name,
      userID: currentUser.UserID,
      sendByEmail: sendByTool.sendByEmail,
      sendByWsp: sendByTool.sendByWsp
    }
    dispatch(setGlobalLoading(true));
    try {
      const result = await manageFdbkRequestAPI(apiData);
      if (result.error == false) {
        if (sendByTool.sendByEmail) {
          toast.success("Email sent for sentiment request.")
        }
        else {
          toast.success("Whatsapp message sent for sentiment request.")
        }
      }
    } catch (err) {
      console.error('Error while sending sentiment request', err)
    } finally {
      dispatch(setGlobalLoading(false));
    }
  }

  // Contact card component
  const ContactsCard = () => {
    return (
      <div className="space-y-3">
        <div className={`${connDetailsClasses.flexBetween}`}>
          <div className="flex items-center gap-1">
            <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>Contact Persons</h3>
            {hasAgencyAccess && contacts && contacts.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDialogStates((prev) => ({ ...prev, editContactsDialog: true }))
                }}
                className={`h-8 w-8 p-0 ${connDetailsClasses.editButton}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
          {hasAgencyAccess && <Button onClick={() => setDialogStates((prev) => ({ ...prev, otherContacts: true }))}>
            <Plus className="h-4 w-4" />
            Add Contact Person
          </Button>}
        </div>

        <div className="max-h-[300px] overflow-y-auto">
          <div className="space-y-3">
            {contacts.map((contact, index) => {
              const isPrimary = index === 0;
              return (
                <div
                  key={contact.CID || index}
                  className={`p-3 ${connDetailsClasses.cardBase}`}
                >
                  <div className={`${connDetailsClasses.flexBetween}`}>
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-8 w-8 bg-sahi-blue-light rounded-full ${connDetailsClasses.flexCenter}`}
                      >
                        <User className="h-4 w-4 text-sahi-blue" />
                      </div>

                      {/* Contact details */}
                      <div>
                        <p
                          className={`text-sm font-medium ${connDetailsClasses.textColor} flex items-center`}
                        >
                          {contact.CNm || "Unknown Contact"}

                          {isPrimary && (
                            <Star
                              className="h-4 w-4 inline-block ml-1"
                              style={{ color: "var(--sahi-gold)" }}
                              fill="var(--sahi-gold)"
                            />
                          )}
                        </p>

                        {/* Show designation for all contacts */}
                        {contact.Dsgn && (
                          <p className={`text-xs ${connDetailsClasses.textSubColor}`}>
                            {contact.Dsgn}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right side actions */}
                    <div className="flex items-center gap-2">
                      <ContactActions
                        connectionData={{
                          connID: connection?.ConnID || "",
                          contactName: contact.CNm || "",
                          bizName: connection?.BizName || "",
                          mobile: contact.MNo || "",
                          email: contact.Eml || "",
                          cc: isPrimary ? connection?.CC : (typeof contact.CC === "string" ? parseInt(contact.CC, 10) : contact.CC),
                        }}
                        showTemplate={connection?.ConnType.includes('C') ? true : false}
                        connID={connection?.ConnID}
                        onRecordAdded={refreshRecordsAndTransactions}
                      />
                    </div>
                  </div>

                  <div className="flex flex-row gap-2 flex-wrap">
                    {contact?.Eml && (
                      <div
                        className={`inline-flex items-center gap-2 bg-sahi-disable rounded text-xs md:text-sm mt-2`}
                      >
                        <div
                          className="flex flex-row gap-2 justify-center items-center bg-gray-100 hover:bg-gray-200 cursor-pointer px-2 py-1 sahi-radius text-sm text-foreground transition-colors select-none"
                          title="Click to copy email"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(contact.Eml);
                              toast.success("Email copied to clipboard");
                            } catch (err) {
                              console.error("Failed to copy email: ", err);
                            }
                          }}
                        >
                          <BsEnvelopeFill className="h-4 w-4 text-sahi-orange flex-shrink-0" />
                          <span className={`text-gray-900 break-words`}>
                            {contact.Eml}
                          </span>
                        </div>
                      </div>
                    )}
                    {contact?.MNo && (
                      <div
                        className={`inline-flex items-center gap-2 bg-sahi-disable rounded text-xs md:text-sm mt-2`}
                      >
                        <div
                          className="flex flex-row gap-2 justify-center items-center bg-gray-100 hover:bg-gray-200 cursor-pointer px-2 py-1 sahi-radius text-sm text-foreground transition-colors select-none"
                          title="Click to copy phone number"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(contact.MNo);
                              toast.success("Phone number copied to clipboard");
                            } catch (err) {
                              console.error("Failed to copy phone: ", err);
                            }
                          }}
                        >
                          <MdPhone className="h-4 w-4 text-sahi-black flex-shrink-0" />
                          <span className={`text-gray-900 break-words`}>
                            {contact?.MNo}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {contacts.length === 1 && !contacts[0].CNm && (
              <div
                className={`${connDetailsClasses.flexCenter} p-6 ${connDetailsClasses.cardBase}`}
              >
                <div className="text-center">
                  <User className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className={`text-sm ${connDetailsClasses.textSubColor}`}>
                    No contacts available
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  };

  // Helper function to parse custom fields
  const parseCustomFields = (customFieldsString) => {
    if (!customFieldsString || customFieldsString.trim() === '') return [];
    try {
      return JSON.parse(customFieldsString);
    } catch (error) {
      console.error("Error parsing custom fields:", error);
      return [];
    }
  };

  // Function to render tables in dynamic order based on data availability
  const renderTablesInOrder = () => {
    const tableConfigs = [
      ...(!isSupplierOrOther ? [
        {
          component: ThreadsTable,
          hasData: getThreadsData().length > 0,
          key: 'threads',
          alwaysShow: true // Always show ThreadsTable
        },
        {
          component: OrdersHistoryCard,
          hasData: allOrders.length > 0,
          key: 'ordersHistory',
          alwaysShow: false // Only show OrdersHistoryCard when there's data
        }
      ] : []),
      {
        component: TasksTable,
        hasData: getTasksData().length > 0,
        key: 'tasks'
      },
      {
        component: ContractsTable,
        hasData: contracts && contracts.length > 0,
        key: 'contracts'
      },
      {
        component: TransactionTable,
        hasData: transactions && transactions.length > 0,
        key: 'transactions'
      }
    ];

    // Separate tables - always show ones with alwaysShow flag, then others with data
    const alwaysShowTables = tableConfigs.filter(config => config.alwaysShow);
    const tablesWithData = tableConfigs.filter(config => !config.alwaysShow && config.hasData);
    const tablesWithoutData = tableConfigs.filter(config => !config.alwaysShow && !config.hasData);

    // Combine: always show first, then tables with data, then tables without data
    const orderedTables = [...alwaysShowTables, ...tablesWithData, ...tablesWithoutData];

    return orderedTables.map(({ component: Component, key }) => (
      <Component key={key} />
    ));
  };

  // Details card component
  const DetailsCard = () => {
    const customFields = parseCustomFields(connection?.CustomFields);
    const hasNotes = connection?.Notes && connection.Notes.trim() !== '';

    if (!connection?.SinceDT && !connection?.Source && !connection?.Segment && !connection?.City && !connection?.State && connection?.Potential == 0
      && !connection?.TaxID && !hasNotes && (!connection?.Tags || connection?.Tags.trim() === '') && customFields.length === 0 && !connection.RegionID
      && connection?.ConnType !== 'C') {
      return
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-1">
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>Details</h3>
          {isOwnAgencyConnection && (canEditConnections === 1 || canEditConnections === 2) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDialogStates((prev) => ({ ...prev, editDialog: true }))}
              className={`h-8 w-8 p-0 ${connDetailsClasses.editButton}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
        <Card className={`border-sahi-border-color bg-white max-h-[400px] overflow-hidden !pt-0`}>
          <CardContent className="p-3 max-h-[400px] overflow-y-auto">
            <div className="space-y-3">
              <div className={[
                (connection?.SinceDT || connection?.Source) || connection?.Segment,
                (() => {
                  const strLocation = [connection?.City, connection?.State, connection?.CountryID === agencyData?.CountryID ? null : connection?.Country].filter(Boolean).join(", ");
                  return strLocation || connection?.Potential > 0;
                })(),
                connection?.TaxID || (connection?.Notes && connection.Notes.trim() !== '') || parseCustomFields(connection?.CustomFields).length > 0,
              ].filter(Boolean).length > 1 ? "space-y-2" : "space-y-2"}>
                <div className="flex flex-wrap gap-2">
                  {(connection?.SinceDT || connection?.Source) && (
                    <div className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground">
                      {connection?.Source ? (
                        <>
                          <span>Since </span>
                          {connection?.SinceDT && (
                            <span className="font-semibold text-gray-900">
                              {getDateWithoutTime(connection.SinceDT)}
                            </span>
                          )}
                          <span> from </span>
                          <span className="font-semibold text-gray-900">{connection.Source}</span>
                        </>
                      ) : (
                        <>
                          <span>Since </span>
                          <span className="font-semibold text-gray-900">
                            {getDateWithoutTime(connection.SinceDT)}
                          </span>
                        </>
                      )}
                    </div>
                  )}


                  {/* Sentiment Badge */}
                  {connection?.ConnType.includes('C') && (
                    <>
                      {/* Show tooltip for cases with only cancel/close button */}
                      {(!connection.Email || isFeedbackSentToday || lastFeedbackDT) ? (
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <div
                              className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground hover:cursor-pointer"
                              {...((!isFeedbackSentToday && canDeleteConnections && canCreateConnections && canEditConnections === 1)
                                ? {
                                  onClick: () =>
                                    setDialogStates((prev) => ({ ...prev, fdbkRequest: true })),
                                }
                                : {})}
                            >
                              <span>Sentiment: </span>
                              <span className="font-semibold text-gray-900">
                                {getFdbkScoreText(connection.FdbkScore)} {sentimentEmoji(connection.FdbkScore)}
                              </span>
                            </div>
                          </HoverCardTrigger>

                          <HoverCardContent className="bg-sahi-primary-color sahi-radius shadow-2xl p-3 w-auto text-left" side="top">
                            <p className="italic text-sahi-white text-sm">
                              {isFeedbackSentToday
                                ? "You've already sent a sentiment request today."
                                : lastFeedbackDT
                                  ? `Last requested on ${format(lastFeedbackDT, "dd-MMM-yy")}`
                                  : "Click to send a sentiment request."}
                            </p>
                          </HoverCardContent>
                        </HoverCard>
                      ) : (
                        /* Show clickable badge that opens AlertBox for actionable cases */
                        <div
                          className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground hover:cursor-pointer"
                          {...(canDeleteConnections && canCreateConnections && canEditConnections === 1
                            ? {
                              onClick: () =>
                                setDialogStates((prev) => ({ ...prev, fdbkRequest: true })),
                            }
                            : {})}
                        >
                          <span>Sentiment: </span>
                          <span className="font-semibold text-gray-900">
                            {getFdbkScoreText(connection.FdbkScore)} {sentimentEmoji(connection.FdbkScore)}
                          </span>
                        </div>
                      )}

                      {/* Keep the AlertBox for actionable cases */}
                      {/* <AlertBox
                        open={dialogStates.fdbkRequest}
                        onOpenChange={(open) =>
                          setDialogStates((prev) => ({ ...prev, fdbkRequest: open }))
                        }
                        title="Sentiment"
                        content={
                          <div>
                            {hasOwnEmail ? (
                              <p className="mb-1">Do you want to ask for sentiment?</p>
                            ) : (
                              <p className="mb-1">
                                Please configure your email address from masters.
                              </p>
                            )}
                          </div>
                        }
                        className={isDesktop ? "w-[400px]" : ""}
                        actions={
                          hasOwnEmail
                            ? [
                              {
                                label: "No",
                                variant: "cancel",
                                value: 0,
                                className: "w-full sm:w-auto",
                                icon: <X className="h-4 w-4" />,
                              },
                              {
                                label: "Yes",
                                variant: "action",
                                value: 1,
                                className: "w-full sm:w-auto",
                                icon: <Check className="h-4 w-4" />,
                              },
                            ]
                            : [
                              {
                                label: "Close",
                                variant: "cancel",
                                value: 0,
                                className: "w-full sm:w-auto",
                                icon: <X className="h-4 w-4" />,
                              },
                              {
                                label: "Configure Email",
                                variant: "action",
                                value: 2,
                                className: "w-full sm:w-auto",
                                icon: <Settings className="h-4 w-4" />,
                              },
                            ]
                        }
                        onAction={(actionValue) => {
                          if (hasOwnEmail) {
                            if (actionValue === 1) {
                              handleFdbkRequest();
                            }
                          } else {
                            if (actionValue === 2) {
                              setDialogStates((prev) => ({ ...prev, emailConfig: true }));
                            }
                          }
                        }}
                      /> */}
                      <AlertBox
                        open={dialogStates.fdbkRequest}
                        onOpenChange={(open) =>
                          setDialogStates((prev) => ({ ...prev, fdbkRequest: open }))
                        }
                        title="Request Sentiment"
                        content={
                          <div>
                            <p className="mb-1">Would you like to ask {connection.BizName || connection.ContactName || 'Company'} how their experience is as your customer?</p>
                          </div>
                        }
                        className={isDesktop ? "w-[400px]" : ""}
                        actions={[
                          {
                            label: "No",
                            variant: "cancel",
                            value: 0,
                            className: "w-full sm:w-auto",
                            icon: <X className="h-4 w-4" />,
                          },
                          {
                            label: "Yes",
                            variant: "action",
                            value: 1,
                            className: "w-full sm:w-auto",
                            icon: <Check className="h-4 w-4" />,
                          },
                        ]
                        }
                        onAction={(actionValue) => {
                          if (actionValue === 1) {
                            handleFdbkRequest();
                          }
                        }}
                      />
                    </>
                  )}

                  {connection?.Segment && (() => {
                    const segmentData = masterData?.segments?.find((s) => s.CNm === connection.Segment);
                    const segmentColor = segmentData?.Clr ? `#${segmentData.Clr}` : null;

                    return (
                      <div className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground">
                        <div className="flex items-center gap-2">
                          {segmentColor && (
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: segmentColor }}
                            />
                          )}
                          <div className="flex items-baseline">
                            <span className="font-semibold text-gray-900">{connection.Segment}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {(() => {
                  const strLocation = [
                    connection?.City,
                    connection?.State,
                    connection?.CountryID === agencyData?.CountryID ? null : connection?.Country
                  ].filter(Boolean).join(", ");
                  const currentRegion = regions.find(rgn => rgn.RID === connection?.RegionID)?.Rgn;
                  const hasPotential = connection?.Potential > 0;

                  if (!strLocation && !currentRegion && !hasPotential) return null;

                  return (
                    <div className="flex flex-wrap gap-2">
                      {strLocation && (
                        <div className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-sahi-dark-blue" />
                          <span className="font-semibold text-gray-900">{strLocation}</span>
                        </div>
                      )}

                      {currentRegion && (
                        <div className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground">
                          <span>Region: </span>
                          <span className="font-semibold text-gray-900">{currentRegion}</span>
                        </div>
                      )}

                      {hasPotential && (
                        <div className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground">
                          <FaSackDollar className="h-3.5 w-3.5 flex-shrink-0 text-sahi-green" />
                          <span className="font-semibold text-gray-900 tabular-nums">
                            {currencySymbol} {connection?.Potential ? connection.Potential.toLocaleString("en-US") : ""}
                          </span>
                          <span className="text-foreground">/yr</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {(connection?.TaxID || hasNotes || customFields.length > 0) && (
                  <div className="flex flex-wrap gap-2">
                    {connection?.TaxID && (
                      <div className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground">
                        <span>{taxIDLabel}: </span>
                        <span className="font-semibold text-gray-900">{connection.TaxID}</span>
                      </div>
                    )}

                    {/* Custom Fields */}
                    {customFields.map((field, index) => (
                      <div key={index} className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground">
                        <span>{field.F}: </span>
                        <span className="font-semibold text-gray-900">{field.V}</span>
                      </div>
                    ))}

                    {/* Notes will appear on the same line if short, or wrap to next line if long */}
                    {hasNotes && (
                      <div className="inline-block bg-gray-100 px-3 py-2 sahi-radius text-sm text-foreground leading-normal">
                        Notes: <span className="font-semibold">{connection.Notes}</span>
                      </div>
                    )}
                  </div>
                )}

              </div>

              {connection?.Tags && connection.Tags.trim() !== '' && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-1">
                    {connection.Tags.split(',').map((tag, index) => {
                      const trimmedTag = tag.trim();
                      if (!trimmedTag) return null;

                      return (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 sahi-radius"
                        >
                          #{trimmedTag}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Business card component
  const BusinessCardCard = () => {
    const businessCardPath = connection?.ContactCard || connection?.Attachment || connection?.attachment;
    if (!businessCardPath) return null;

    const handleEditBusinessCard = () => {
      setDialogStates((prev) => ({ ...prev, editDialog: true }));
      // Wait for the edit dialog to mount before scrolling to its business card section.
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById('business-card-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (attempts < 10) {
          attempts += 1;
          setTimeout(tryScroll, 150);
        }
      };
      setTimeout(tryScroll, 150);
    };

    const handleImageLoad = (e) => {
      const { naturalWidth, naturalHeight } = e.target;
      if (naturalWidth && naturalHeight) {
        setBusinessCardRatio(naturalWidth / naturalHeight);
      }
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-1 justify-between">
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>Business Card</h3>
          {isOwnAgencyConnection && (canEditConnections === 1 || canEditConnections == 2) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEditBusinessCard}
              className={`h-7 w-7 p-0 flex-shrink-0 ${connDetailsClasses.editButton}`}
              title="Edit Business Card"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Card className="border-sahi-border-color bg-white overflow-hidden p-3">
          {businessCardRatio === null ? (
            <div className="relative group w-full rounded-lg border border-gray-100 bg-gray-50 overflow-hidden shadow-sm h-[180px] flex items-center justify-center animate-pulse">
              <span className="text-xs text-gray-400">Loading business card...</span>
              <img
                src={getUploadUrl(`Uploads/${businessCardPath}`)}
                alt="Business Card"
                className="absolute opacity-0 w-0 h-0"
                onLoad={handleImageLoad}
              />
            </div>
          ) : businessCardRatio >= 1.25 ? (
            <div className="relative group w-full rounded-lg border border-gray-100 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img
                src={getUploadUrl(`Uploads/${businessCardPath}`)}
                alt="Business Card"
                className="w-full h-auto block cursor-zoom-in rounded-lg"
                onClick={() => {
                  setFullPreviewUrl(getUploadUrl(`Uploads/${businessCardPath}`));
                  setIsFullPreviewLoading(true);
                  setIsFullPreviewOpen(true);
                }}
              />
            </div>
          ) : (
            <div className="relative group w-full rounded-lg border border-gray-100 bg-gray-50 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex items-center justify-center">
              <img
                src={getUploadUrl(`Uploads/${businessCardPath}`)}
                alt="Business Card"
                className="w-full h-[180px] object-contain cursor-zoom-in rounded-lg"
                onClick={() => {
                  setFullPreviewUrl(getUploadUrl(`Uploads/${businessCardPath}`));
                  setIsFullPreviewLoading(true);
                  setIsFullPreviewOpen(true);
                }}
              />
            </div>
          )}
        </Card>
      </div>
    );
  };

  // --- ACCESS SECTION COMPONENTS ---
  const AccessCard = () => {
    // 1. Filter always-visible users (Admins, FullAccess, ConnViewTypeID=1)
    const alwaysVisibleUsers = resolvedUsers.filter(u =>
      u.isAdmin || u.fullAccess || u.connViewTypeID === 1
    );

    // 2. Filter removable users (ConnViewTypeID=4 AND in current POC list)
    // Note: We use accessUserIDs state which should be initialized from connection.POCUserIDs
    const removableUsers = resolvedUsers.filter(u =>
      u.connViewTypeID === 4 && accessUserIDs.includes(u.userID)
    );

    return (
      <div className="space-y-3">
        <div className={connDetailsClasses.flexBetween}>
          <h3 className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>Access</h3>
          {isOwnAgencyConnection && hasAgencyAccess && <Button
            onClick={() => {
              const eligibleUsers = resolvedUsers.filter(u => u.connViewTypeID === 4 && !u.isAdmin && !u.fullAccess && u.userID !== connection?.POCUserID);
              const availableUsers = eligibleUsers.filter(u => !accessUserIDs.includes(u.userID));

              if (availableUsers.length === 0) {
                setDialogStates((prev) => ({ ...prev, allUsersHaveAccessAlert: true }));
              } else {
                setDialogStates((prev) => ({ ...prev, accessDialog: true }));
              }
            }}
            variant="ghost"
            size="sm"
            className={`h-8 w-8 p-0 ${connDetailsClasses.editButton}`}
          >
            <Plus className="h-4 w-4" />
          </Button>}
        </div>

        <Card className={`border-sahi-border-color bg-white !pt-0 `}>
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-2">
              {/* Always visible users (No cross icon) */}
              {alwaysVisibleUsers.map(user => (
                <span
                  key={`static-${user.userID}`}
                  className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-gray-100 hover:bg-gray-200 h-7 sahi-badge"
                >
                  {user.name}
                </span>
              ))}

              {/* Removable users (With cross icon) */}
              {removableUsers.map(user => (
                <span
                  key={`removable-${user.userID}`}
                  className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-gray-100 hover:bg-gray-200 h-7 sahi-badge"
                >
                  {user.name}
                  {isOwnAgencyConnection && hasAgencyAccess && <X
                    className="ml-2 h-3 w-3 cursor-pointer text-gray-500 hover:text-sahi-red"
                    onClick={() => setRemoveAccessState({ open: true, userID: user.userID })}
                  />}
                </span>
              ))}

              {alwaysVisibleUsers.length === 0 && removableUsers.length === 0 && (
                <div className="text-sm text-sub-text-color italic">No access defined. Click + to add users.</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };
  // Loading shimmer component for records
  const RecordShimmer = () => (
    <div className="space-y-3">
      <div className={`${connDetailsClasses.cardBase} p-3 animate-pulse`}>
        <div className="h-4 bg-gray-200 sahi-radius w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 sahi-radius w-1/2 mb-3"></div>
        <div className={connDetailsClasses.flexBetween}>
          <div className="h-3 bg-gray-200 sahi-radius w-20"></div>
          <div className="h-3 bg-gray-200 sahi-radius w-24"></div>
        </div>
      </div>
    </div>
  )

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className={`w-full h-full max-w-full max-h-full sm:!max-w-[80vw] sm:!w-[80vw] ${(!showModule.transactions || !showModule.contracts)
            ? 'sm:!h-auto sm:!max-h-[95vh] pb-4'
            : 'sm:!min-h-[80vh] sm:!max-h-[95vh]'
            } overflow-hidden flex flex-col gap-0`}
          style={{ backgroundColor: 'var(--background)' }}
        >
          <DialogHeader className="border-b border-sahi-border-color pb-4 flex-shrink-0">
            {/* Mobile Header */}
            <div className="flex items-center w-full sm:hidden">
              <DialogClose onClick={onClose} className="p-2 -ml-2">
                <ArrowLeft className={`h-6 w-6 ${connDetailsClasses.textColor}`} />
                <span className="sr-only">Back</span>
              </DialogClose>
              <div className="flex-1 text-center">
                <DialogTitle className={`text-lg font-semibold ${connDetailsClasses.textColor}`}>
                  {`${connection?.ConnType === 'C' ? 'Customer' :
                    connection?.ConnType === 'S' ? 'Supplier' :
                      connection?.ConnType === 'O' ? 'Other' :
                        connection?.ConnType?.includes('C') && connection?.ConnType?.includes('S') ? 'Contact' :
                          'Contact'} Details - ${connection?.BizName || connection?.ContactName || "Company Name"}`}
                </DialogTitle>
              </div>
              {isOwnAgencyConnection && (canEditConnections === 1 || canEditConnections == 2) && <Button
                variant="ghost"
                size="sm"
                onClick={() => setDialogStates((prev) => ({ ...prev, editDialog: true }))}
                className={`${connDetailsClasses.editButton} p-2`}
              >
                <Pencil className="h-4 w-4" />
              </Button>}
            </div>

            {/* Desktop Header */}
            <div className={`hidden sm:flex items-center justify-between w-full`}>
              <div className="flex items-center gap-3">
                <div>
                  <DialogTitle className={`text-xl ${connDetailsClasses.textColor}`}>
                    {`${connection?.ConnType === 'C' ? 'Customer' :
                      connection?.ConnType === 'S' ? 'Supplier' :
                        connection?.ConnType === 'O' ? 'Other' :
                          connection?.ConnType?.includes('C') && connection?.ConnType?.includes('S') ? 'Contact' :
                            'Contact'} Details - ${connection?.BizName || connection?.ContactName || "Company Name"}`}
                  </DialogTitle>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isOwnAgencyConnection && (canEditConnections === 1 || canEditConnections == 2) &&
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialogStates((prev) => ({ ...prev, editDialog: true }))}
                    className={`h-9 w-9 p-0 text-text-color sm:hover:bg-gray-200`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                }

                <DialogClose
                  onClick={onClose}
                  className={`h-9 w-9 p-0 hover:bg-bg-grey ${connDetailsClasses.flexCenter}`}
                >
                  <X className={`h-4 w-4 ${connDetailsClasses.textColor}`} />
                </DialogClose>
              </div>
            </div>
          </DialogHeader>

          {/* Main Content - Make the entire content scrollable */}
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-no-arrows">
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  {renderTablesInOrder()}
                </div>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <LastInteractionCard />
                    <PendingDetailsCard />
                  </div>
                  <FeedbackSection
                    feedbackScore={connection?.FdbkScore || 0}
                    feedbackNotes={connection?.FdbkNotes || ""}
                    failureReason={feedbackThreadDetail?.thrFailureRsn || ""}
                    isConnection={true}
                    threadDetail={feedbackThreadDetail}
                  />

                  {connection?.ContactName && contacts && contacts.length > 0 && <ContactsCard />}
                  <DetailsCard />
                  <BusinessCardCard />
                  {(currentUser?.IsAdmin || currentUser?.FullAccess) && <AccessCard />}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-view Modal Overlay using nested Dialog to bypass Radix focus/pointer traps */}
      <Dialog open={isFullPreviewOpen} onOpenChange={setIsFullPreviewOpen}>
        <DialogContent className="max-w-[100vw] h-screen max-h-screen bg-black/50 p-0 border-0 flex items-center justify-center z-[10000]">
          {/* Close Button */}
          <button
            type="button"
            className="absolute top-6 right-6 text-white hover:text-gray-300 p-2 rounded-full transition-colors focus:outline-none z-[10001] cursor-pointer bg-black/40 hover:bg-black/60 flex items-center justify-center"
            onClick={() => setIsFullPreviewOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>

          {/* Spinner */}
          {isFullPreviewLoading && (
            <div className="absolute flex flex-col items-center gap-2 z-[10002]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
              <span className="text-xs text-white/70">Loading image...</span>
            </div>
          )}

          <div
            className="w-full h-full flex items-center justify-center p-4 cursor-pointer"
            onClick={() => setIsFullPreviewOpen(false)}
          >
            <img
              src={fullPreviewUrl}
              alt="Full Preview"
              className={`max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${isFullPreviewLoading ? 'opacity-0 h-0 w-0' : 'opacity-100'
                }`}
              onLoad={() => setIsFullPreviewLoading(false)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachment Preview Modal for Sales/Orders & Other Documents */}
      <AttachmentPreviewModal
        open={Boolean(previewFilePath)}
        onOpenChange={(open) => {
          if (!open) setPreviewFilePath(null);
        }}
        filePath={previewFilePath}
      />

      <ThreadEntry
        triggerButton={false}
        threadTypeID={(!dialogStates.isInquiryOnlyMode) ? editingThreadTypeID ? editingThreadTypeID : localStates.selectedThreadType : localStates.selectedThreadType}
        threadID={editingThreadID}
        open={dialogStates.threadEntry}
        onOpenChange={async (open) => {
          setDialogStates((prev) => ({ ...prev, threadEntry: open, ordHistoryEdit: open ? prev.ordHistoryEdit : false }));
          if (!open) {
            setEditingThreadID(null);
            setEditingThreadTypeID(null);
            if (selectedThread) {
              setThreadDetailDialog(true);
            }
            await refreshRecordsAndTransactions(); // Refresh data including orders when closing
            setThrEntryMode(null);
          }
        }}
        preselectedConnID={connection?.ConnID || connection?.id}
        preselectedContactPerson={connection?.ContactName || ""}
        fromConnectionDetail={true}
        calledFrom={!isInqOnlyMode ? '' : localStates.selectedThreadType == 2 ? (thrEntryMode == 'INQDONE' ? 'INQDONE' : thrEntryMode == 'OPEN_ORD' ? '' : 'ORDDONE') : ''}
        allowOpenOrders={thrEntryMode === 'OPEN_ORD'}
        prefilledData={dialogStates.prefilledData}
        extOperation={dialogStates.ordHistoryEdit ? 'EDIT' : ''}
      />

      <TransactionEntry
        isOpen={dialogStates.transactionEntry}
        onClose={() => {
          setDialogStates(prev => ({ ...prev, transactionEntry: false }));
          // Clear edit states when closing
          setSelectedTransaction(null);
          setTransactionPrefilledData({});
        }}
        transactionTypeID={selectedTransactionType}
        connID={connID}
        companyName={connection?.BizName || ""}
        prefilledData={transactionPrefilledData}
        transactionID={selectedTransaction?.XnID || null}
        onTransactionSaved={() => {
          // Refresh transactions data here
          refreshRecordsAndTransactions();
        }}
      />

      <ContractEntry
        isOpen={dialogStates.contractEntry}
        onClose={() => {
          setDialogStates(prev => ({ ...prev, contractEntry: false, contractRenewal: false, contractCancellation: false }));
          // Clear edit states when closing
          setSelectedContract(null);
          setContractPrefilledData({});
        }}
        connID={connID}
        companyName={connection?.BizName || ""}
        prefilledData={{
          ...contractPrefilledData,
          contractTypeSelection: selectedContractType // Pass the contract type selection (sold/purchase)
        }}
        contractID={selectedContract?.ContractID || null}
        isRenewalMode={dialogStates.contractRenewal}
        isCancellationMode={dialogStates.contractCancellation}
      />


      <TaskEntry
        key={editingTaskID || 'new-task'}
        dialogTitle={editingTaskID ? 'Edit Task' : 'Enter Task'}
        triggerButton={false}
        open={dialogStates.taskEntry}
        onOpenChange={(open) => {
          setDialogStates((prev) => ({ ...prev, taskEntry: open }));
          if (!open) {
            setEditingTaskID(null);
            setTaskEntryMode('normal');
            setRepeatPrefillData(null);
            if (selectedTask) {
              setTaskDetailDialog(true);
            }
          }
        }}
        prefilledData={taskEntryMode === 'repeat' ? repeatPrefillData : editingTaskID ? (() => {
          const tasks = getTasksData();
          const task = tasks.find(t => t.TskID === editingTaskID);
          return task ? {
            ...formatTaskData(task),
            connID: connection?.ConnID,
            connectionLocked: true,
            connectionLabel: 'Contact',
            bizName: connection?.BizName || '',
            contactName: connection?.ContactName || '',
            threads: task.TIDs ? task.TIDs.split(',') : '',
            ctrcIDs: task.CIDs ? task.CIDs.split(",") : '',
            secConnIDs: task.SCIDs ? task.SCIDs.split(",").map(id => Number(id)).filter(Boolean) : ''
          } : {
            connID: connection?.ConnID,
            connectionLocked: true,
            connectionLabel: 'Contact',
            bizName: connection?.BizName || '',
            contactName: connection?.ContactName || '',
            threads: '',
            ctrcIDs: '',
            secConnIDs: ''
          };
        })() : (() => {
          return {
            connID: connection?.ConnID,
            connectionLocked: true,
            connectionLabel: 'Contact',
            bizName: connection?.BizName || '',
            contactName: connection?.ContactName || '',
            mobile: connection?.Mobile || '',
            email: connection?.Email || '',
            threads: '',
            connectionType: connection?.ConnType || '',
            source: connection?.Source || '',
            segment: connection?.Segment || '',
          };
        })()}
        contracts={contracts.length == 0 ? [] : contracts}
      />

      <RecordEntry
        triggerButton={false}
        open={dialogStates.recordEntry}
        onOpenChange={(open) => {
          setDialogStates((prev) => ({ ...prev, recordEntry: open }))
          if (!open) {
            setSelectedTaskForRecord(null)
          }
        }}
        prefilledData={{
          connID: connection?.ConnID,
          connectionLocked: true,
          bizName: connection?.BizName || '',
          contactName: connection?.ContactName || '',
          mobile: connection?.Mobile || '',
          email: connection?.Email || '',
          connectionType: connection?.ConnType || '',
          threadIDs: selectedTaskForRecord ? (selectedTaskForRecord.TIDs ? selectedTaskForRecord.TIDs.split(",") : []) : [],
        }}
        selectedTaskForRecord={selectedTaskForRecord ? formatTaskData(selectedTaskForRecord) : null}
        onRecordAdded={async () => {
          await refreshRecordsAndTransactions();
          if (pendingRepeatAfterRecordRef.current) {
            pendingRepeatAfterRecordRef.current = false;
            openRepeatTaskEntry(selectedTaskForRecord);
          }
        }}
        showUpdateStatus={true}
      />

      {dialogStates.editContactsDialog && (
        <ConnectionEntryDialog
          connID={connection?.ConnID}
          triggerButton={false}
          triggerText="Edit Contacts"
          editMode="CONTACTS_ONLY"
          open={dialogStates.editContactsDialog}
          onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, editContactsDialog: open }))}
          parentDialogSetter={onClose}
          onConnectionSaved={refreshConnectionData}
        />
      )}

      {dialogStates.editDialog && (
        <ConnectionEntryDialog
          connID={connection?.ConnID}
          triggerButton={false}
          triggerText="Edit Contact"
          editMode="CONNECTION_ONLY"
          open={dialogStates.editDialog}
          onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, editDialog: open }))}
          parentDialogSetter={onClose}
          onConnectionAdded={handleThreadUpdated}
        />
      )}

      <RecordHistoryDialog
        isOpen={dialogStates.recordHistory}
        onClose={() => setDialogStates((prev) => ({ ...prev, recordHistory: false, recordTypeFilter: null }))}
        records={records}
        threadData={allThreads}
        threadTypes={threadTypesData}
        getExecutiveName={getExecutiveName}
        getRelativeDateText={getRelativeDateText}
        connectionName={connection?.BizName || connection?.ContactName}
        connID={connection?.ConnID}
        onRefreshRecords={handleThreadUpdated}
        recordTypeFilter={dialogStates.recordTypeFilter}
        pendingRIDs={userPendingRIDs}
        onRecordEntryOpen={() => {
          setDialogStates((prev) => ({
            ...prev,
            recordHistory: false,
            recordEntry: true
          }))
        }}
      />

      <Upcoming
        open={dialogStates.upcoming}
        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, upcoming: open }))}
      />

      {/* Email config dialog */}
      <Policies
        open={dialogStates.emailConfig}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, emailConfig: open }))}
        configType="EMAIL"
      />

      <TaskDetailDialog
        isOpen={taskDetailDialog}
        onClose={() => {
          setTaskDetailDialog(false);
          setSelectedTask(null);
        }}
        taskID={selectedTask?.TskID}
        openedFromConnectionDetails={true}
      />

      <TaskHistoryDialog
        isOpen={dialogStates.taskHistory}
        onClose={() => setDialogStates((prev) => ({ ...prev, taskHistory: false }))}
        enrichedConnection={connection}
        threadData={allThreads}
        threadTypes={threadTypesData}
        getExecutiveName={getExecutiveName}
        getRelativeDateText={getRelativeDateText}
        getPriorityInfo={getPriorityInfo}
        getColorClass={getColorClass}
        getTaskColor={getTaskColor}
        onTaskEntryOpen={() => {
          setDialogStates((prev) => ({
            ...prev,
            taskHistory: false,
            taskEntry: true
          }))
        }}
      />

      <OtherContactsEntry
        connID={connection?.ConnID}
        existingContacts={contacts.slice(1)}
        isOpen={dialogStates.otherContacts}
        onClose={() => setDialogStates((prev) => ({ ...prev, otherContacts: false }))}
        bizName={connection?.BizName || "Contact"}
      />

      <TaskCompleteDialog
        open={dialogStates.confirmCompleteAlert}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCompleteAlert: open }))}
        task={selectedTaskForRecord}
        onAction={(actionValue, repeat) => {
          if (actionValue === "complete") {
            handleAction(selectedTaskForRecord, 'DONE', 'task');
            if (repeat) openRepeatTaskEntry(selectedTaskForRecord);
          } else if (actionValue === "record") {
            pendingRepeatAfterRecordRef.current = repeat;
            handleItemAction('RECORD_AND_COMPLETE', selectedTaskForRecord, 'task');
          } else {
            setSelectedTaskForRecord(null);
          }
        }}
      />

      {/* Task Cancel/Reject Alert */}
      <TaskCompleteDialog
        mode={(() => {
          if (!selectedTaskForRecord) return "cancel";
          const myUserIDs = getMyAgenciesMappedIDs('UserID');
          const taskCreatorID = Number(selectedTaskForRecord.CreatedUID || selectedTaskForRecord.UID || 0);
          const isMyCreator = myUserIDs.includes(taskCreatorID);
          const isMyAssignee = myUserIDs.includes(Number(selectedTaskForRecord.UID)) || Number(selectedTaskForRecord.UID) === Number(currentUserID);
          return (isMyAssignee && !isMyCreator) ? "reject" : "cancel";
        })()}
        open={dialogStates.confirmCancelAlert}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCancelAlert: open }))}
        task={selectedTaskForRecord}
        className="w-[400px]"
        onAction={(actionValue) => {
          if (actionValue === "yes") {
            handleAction(selectedTaskForRecord, 'CANCEL', 'task');
          } else {
            setSelectedTaskForRecord(null);
          }
        }}
      />

      {/* Thread Complete Alert */}
      <CompleteThreadDialog
        typeID={selectedThreadForAction?.TypeID}
        open={dialogStates.confirmThreadCompleteAlert}
        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, confirmThreadCompleteAlert: open }))}
        connID={connection?.ConnID}
        onComplete={({ shareByEmail, shareByWhatsApp, takeFeedback }) => handleAction(selectedThreadForAction, 'DONE', 'thread', shareByEmail, takeFeedback, '', false, shareByWhatsApp)}
        showFeedbackOption={true}
      />

      <CancelThreadDialog
        open={dialogStates.confirmThreadCancelAlert}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmThreadCancelAlert: open }))}
        onConfirm={(rejReason, isMarkCold) => {
          handleAction(selectedThreadForAction, 'CANCEL', 'thread', false, false, rejReason, isMarkCold);
          setSelectedThreadForAction(null);
        }}
        typeID={selectedThreadForAction?.TypeID}
        currStageID={selectedThreadForAction?.CurrStageID || selectedThreadForAction?.currStageID}
        rejectionReasonOptions={rejectionReasonOptions}
        disqlRsns={(() => {
          if (!masterData?.disqlReasons || !selectedThreadForAction?.TypeID) return [];
          try {
            const currentThreadReasons = masterData.disqlReasons.find(item => item.TID === selectedThreadForAction.TypeID);
            if (!currentThreadReasons || !currentThreadReasons.Rsns) return [];
            return JSON.parse(currentThreadReasons.Rsns);
          } catch (error) {
            console.error("Error parsing disqualification reasons:", error);
            return [];
          }
        })()}
        requireReason={true}
        connID={selectedThreadForAction?.ConnID}
      />

      {/* Hidden file input for attachment upload */}
      <input
        type="file"
        ref={attachFileInputRef}
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) {
            const success = await handleUploadAttachment(file);
            if (!success) {
              setAttachDocDialog(true);
            }
          }
          e.target.value = '';
        }}
      />

      {/* Confirmation AlertDialog for Attaching Document */}
      <AlertDialog
        open={attachDocDialog}
        onOpenChange={(open) => {
          if (!open) {
            setAttachDocDialog(false);
            setCompletedThreadForAttachment(null);
          }
        }}
      >
        <AlertDialogContent className="w-[92%] sm:w-full max-w-md rounded-2xl p-4 bg-white gap-4 border border-zinc-200/80 shadow-2xl">
          <AlertDialogHeader className="text-left gap-1.5">
            <AlertDialogTitle className="text-base sm:text-lg font-semibold text-zinc-900">
              Attach Document / Invoice
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-600 leading-normal">
              Do you want to attach a document or invoice for this sale?
              {/* <span className="font-semibold text-zinc-900">
                "{completedThreadForAttachment?.connectionData?.bizName || completedThreadForAttachment?.connectionData?.contactName || completedThreadForAttachment?.ContactPerson || completedThreadForAttachment?.MainProduct || (completedThreadForAttachment?.Reference ? `Sale #${completedThreadForAttachment.Reference}` : "this sale")}"
              </span> */}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row sm:!justify-start gap-2.5 pt-1">
            <Button
              type="button"
              onClick={() => {
                attachFileInputRef.current?.click();
              }}
              className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <Check className="w-3.5 h-3.5 stroke-[2.5]" /> Yes, Attach
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAttachDocDialog(false);
                setCompletedThreadForAttachment(null);
              }}
              className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ContractDetailDialog
        isOpen={contractDetailDialog}
        onClose={() => {
          setContractDetailDialog(false);
          setSelectedContractForDetail(null);
        }}
        contract={selectedContractForDetail}
        connection={connection}
        currencySymbol={currencySymbol}
        onContractUpdate={(updatedContract) => {
          // Handle contract update if needed
          refreshRecordsAndTransactions();
        }}
      />

      <ThrDetails
        isOpen={dialogStates.threadDetails}
        onClose={(open) => setDialogStates((prev) => ({ ...prev, threadDetails: open, isHistoryview: false }))}
        threadID={selectedThread?.ThreadID}
        isHistoryview={dialogStates.isHistoryview}
        onThreadUpdated={handleThreadUpdated}
        initialData={threadInitialData}
      />

      <ThrDetails
        isOpen={dialogStates.thrDetails}
        onClose={(open) => setDialogStates((prev) => ({ ...prev, thrDetails: open }))}
        threadID={selectedThread?.ThreadID}
        onThreadUpdated={handleThreadUpdated}
      />

      <ReassignDialog
        open={dialogStates.reassignDialog}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, reassignDialog: open }))}
        threadData={selectedThreadForAction}
        onReassign={async (newPOCUserID) => {
          try {
            dispatch(setGlobalLoading(true));
            const result = await reassignThreadAPI(selectedThreadForAction, newPOCUserID);
            if (result?.message === 'Success') {
              const assignedUser = users.find(u => u.userID === Number(newPOCUserID));
              toast.success(`${isInqOnlyMode ? 'Inquiry' : 'Thread'} reassigned to ${assignedUser?.name}`);
              setDialogStates(prev => ({ ...prev, reassignDialog: false }));
              handleThreadUpdated();
            }
          } catch (error) {
            console.error('Error reassigning thread:', error);
            toast.error('Failed to reassign thread');
          } finally {
            dispatch(setGlobalLoading(false));
          }
        }}
      />

      <AlertBox
        open={dialogStates.allUsersHaveAccessAlert}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, allUsersHaveAccessAlert: open }))}
        title="Access Info"
        content={
          <div>
            <p>All users have access to this connection already.</p>
          </div>
        }
        actions={[
          {
            label: "OK",
            variant: "action",
            value: 0,
            className: "w-full sm:w-auto",
            icon: <Check className="h-4 w-4" />,
          }
        ]}
        onAction={() => setDialogStates(prev => ({ ...prev, allUsersHaveAccessAlert: false }))}
      />

      <ManageAccessDialog
        open={dialogStates.accessDialog}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, accessDialog: open }))}
        allUsers={users}
        currentAccessIDs={[
          ...users.filter(u => u.isAdmin || u.fullAccess || u.connViewTypeID === 1).map(u => u.userID),
          ...accessUserIDs
        ]}
        setRemoveAccessState={setRemoveAccessState}
        onSave={async (newID) => {
          try {
            await manageConnectionAccessAPI(connID, 'ACCADD', newID);
            setAccessUserIDs(prev => [...prev, newID]);
            toast.success("Access granted.");
          } catch (error) {
            toast.error("Failed to grant access.");
          }
        }}
      />

      <AlertBox
        open={removeAccessState.open}
        onOpenChange={(open) => setRemoveAccessState(prev => ({ ...prev, open }))}
        title="Remove Access"
        content={
          <div>
            <p>Are you sure you want to remove access of this connection from <strong>{users.find(u => u.userID === removeAccessState.userID)?.name}</strong>?</p>
          </div>
        }
        actions={[
          {
            label: "No",
            variant: "cancel",
            value: 0,
            icon: <X className="h-4 w-4" />,
          },
          {
            label: "Yes",
            variant: "action",
            value: 1,
            icon: <Check className="h-4 w-4" />,
          },
        ]}
        onAction={async (val) => {
          if (val === 1) {
            try {
              await manageConnectionAccessAPI(connID, 'ACCRMV', removeAccessState.userID);
              setAccessUserIDs(prev => prev.filter(id => id !== removeAccessState.userID));
              toast.success("Access removed.");
            } catch (error) {
              toast.error("Failed to remove access.");
            }
            setRemoveAccessState({ open: false, userID: null });
          } else {
            setRemoveAccessState(prev => ({ ...prev, open: false }));
          }
        }}
      />
      <ThreadEntry
        triggerButton={false}
        threadTypeID={2}
        threadID={null}
        open={dialogStates.convertToSales}
        onOpenChange={async (open) => {
          setDialogStates((prev) => ({ ...prev, convertToSales: open }));
          if (!open) {
            setSelectedThreadForAction(null);
            await refreshRecordsAndTransactions();
          }
        }}
        preselectedConnID={connection?.ConnID || connection?.id}
        preselectedContactPerson={connection?.ContactName || ""}
        fromConnectionDetail={true}
        calledFrom="INQDONE"
        prefilledData={selectedThreadForAction ? {
          connID: connection?.ConnID,
          contactPerson: connection?.ContactName,
          product: selectedThreadForAction?.mainProduct,
          model: selectedThreadForAction?.model,
          reference: selectedThreadForAction?.reference,
          quantity: selectedThreadForAction?.mainQty,
          businessValue: selectedThreadForAction?.bizProspect,
          notes: selectedThreadForAction?.notes,
          source: selectedThreadForAction?.source,
          inquiryThreadID: selectedThreadForAction?.threadID,
          pocUserID: selectedThreadForAction?.pocUserID,
          sinceDT: selectedThreadForAction?.sinceDT,
        } : {}}
      />
    </TooltipProvider>
  )
}
const ManageAccessDialog = ({ open, onOpenChange, allUsers, currentAccessIDs, onSave, setRemoveAccessState }) => {
  const [selectedUserID, setSelectedUserID] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedUserID("");
    }
  }, [open]);

  const handleSave = () => {
    if (!selectedUserID) return;
    onSave(Number(selectedUserID));
    onOpenChange(false);
  };

  const userOptions = allUsers
    .filter(u => !currentAccessIDs.includes(u.userID))
    .map(u => ({ label: u.name, value: String(u.userID) }));

  // Get executives who already have access
  const currentAccessUsers = allUsers.filter(u =>
    currentAccessIDs.includes(u.userID)
  );

  // Separate always visible and removable users
  const alwaysVisibleUsers = currentAccessUsers.filter(u =>
    u.isAdmin || u.fullAccess || u.connViewTypeID === 1
  );

  const removableUsers = currentAccessUsers.filter(u =>
    u.connViewTypeID === 4 && !u.isAdmin && !u.fullAccess
  );

  const renderHeader = () => (
    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
      <div className="sm:hidden flex items-center justify-between w-full h-8">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
            <ArrowLeft className="h-6 w-6" />
            <span className="sr-only">Back</span>
          </DialogClose>
          <DialogTitle className="text-lg font-medium">
            Manage Access
          </DialogTitle>
        </div>
      </div>
      <DialogTitle className="hidden sm:block text-lg md:text-xl">
        Manage Access
      </DialogTitle>
      <div className="hidden sm:flex items-center gap-2">
        <DialogClose className="flex-shrink-0" tabIndex={-1}>
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </div>
    </DialogHeader>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col sm:!max-w-[400px] sm:min-h-0 sm:max-h-[95vh] sm:h-auto sm:rounded-lg rounded-none">
        {renderHeader()}

        <div className="pb-4 px-4 flex-1 overflow-y-auto space-y-4">
          {/* Current Access Section */}
          {currentAccessUsers.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none">Current Access</label>
              <div className="flex flex-wrap gap-2 bg-white rounded-lg p-3">
                {/* Always visible users (No cross icon) */}
                {alwaysVisibleUsers.map(user => (
                  <span
                    key={`static-${user.userID}`}
                    className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-gray-100 hover:bg-gray-200 h-7 sahi-badge"
                  >
                    {user.name}
                  </span>
                ))}

                {/* Removable users (With cross icon) */}
                {removableUsers.map(user => (
                  <span
                    key={`removable-${user.userID}`}
                    className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-gray-100 hover:bg-gray-200 h-7 sahi-badge"
                  >
                    {user.name}
                    <X
                      className="ml-2 h-3 w-3 cursor-pointer text-gray-500 hover:text-sahi-red"
                      onClick={() => setRemoveAccessState({ open: true, userID: user.userID })}
                    />
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add New Access Section */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Executive</label>
            <CustomSearchableDropdown
              options={userOptions}
              value={selectedUserID}
              onSelect={setSelectedUserID}
              placeholder="Select Executive"
              searchPlaceholder="Search executives..."
              displayKey="label"
              valueKey="value"
              allowAdd={false}
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-start sm:gap-3 w-full">
            <Button
              onClick={handleSave}
              className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[100px]"
              disabled={!selectedUserID}
            >
              <Check className="h-4 w-4" />
              Add
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectionDetails